import AcademicYearLockNotice from '../components/common/AcademicYearLockNotice';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import WorkspaceFindBar from '../components/shared/WorkspaceFindBar';
import { SearchHighlightedText, SearchTextareaMirror } from '../components/shared/SearchTextareaMirror';
import TodoCategoryCreateModal from '../components/todoNote/TodoCategoryCreateModal';
import TodoNoteItemAddModal from '../components/todoNote/TodoNoteItemAddModal';
import TodoRoutineCreateModal from '../components/todoNote/TodoRoutineCreateModal';
import TodoShareBoardCreateModal from '../components/todoNote/TodoShareBoardCreateModal';
import TodoShareBoardEditModal from '../components/todoNote/TodoShareBoardEditModal';
import { SortableTodoCategoryChip } from '../components/todoNote/SortableTodoCategoryChip';
import { SortableRow } from '../components/todoNote/TodoNoteSortableRow';
import { DEFAULT_TODO_CATEGORY_NAME } from '../components/todoNote/todoNoteRowShared';
import {
  applyTodoCategoryModalConfirm,
  mergeDuplicateTodoCategories,
} from '../utils/todoNoteCategoryEdit';
import {
  TodoNoteCalendarItemHoverPop,
  TodoNoteCalendarItemPopContent,
} from '../components/todoNote/TodoNoteCalendarItemHoverPop';
import TodoNoteBoardPicker from '../components/todoNote/TodoNoteBoardPicker';
import TodoNotePostitBoard from '../components/todoNote/TodoNotePostitBoard';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { getCalendarDays } from '../services/calendarService';
import { publishTodoNoteWorkspaceSnapshot } from '../query/noteCacheUpdates';
import { saveTodoNoteWorkspace, type TodoNoteWorkspaceSnapshot } from '../services/todoNoteService';
import { applyTodoFavoriteFlagsFromSource } from '../utils/todoNoteFavorite';
import { loadTodoNoteShareBridge } from '../utils/todoNoteShareBridgeCache';
import { loadTodoNoteWorkspaceCache } from '../utils/todoNoteWorkspaceCache';
import {
  emitTodoNoteBoardSelection,
  TODO_NOTE_BOARD_SELECTION_EVENT,
  type TodoNoteBoardSelectionDetail,
} from '../utils/todoNoteBoardSelectionEvents';
import {
  emitTodoNoteWorkspaceSaved,
  TODO_NOTE_WORKSPACE_SAVED_EVENT,
  type TodoNoteWorkspaceSavedDetail,
} from '../utils/todoNoteWorkspaceSavedEvents';
import {
  TodoShareRevisionConflictError,
  buildTodoShareSavePayload,
  buildTodoShareSavedFingerprint,
  pickTodoShareChangedForSave,
  type TodoShareSavedFingerprint,
  createTodoShareBoard,
  deleteTodoShareBoard,
  getTodoSharePublicUrl,
  listTodoShareBoards,
  loadTodoShareAsOwner,
  loadTodoSharePostitLayout,
  migratePersonalTodoToDefaultBoard,
  renameTodoShareBoard,
  rotateTodoShareCode,
  saveTodoShareAsOwner,
  setTodoShareBoardActive,
  type TodoShareBoardMeta,
} from '../services/todoShareService';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft, TodoNoteItemView } from '../types/todoNote';
import { sortTodoItemsDrafts, todoItemBucketKey } from '../utils/todoNoteBucket';
import {
  expandTodoItemsForView,
  normalizeRoutineDayLogs,
  parseRoutineOccurrenceId,
  patchRoutineDayLog,
  toggleRoutineOccurrenceComplete,
} from '../utils/todoRoutine';
import {
  findTimetableAutoScrollItemId,
  scrollTodoNoteSearchMatchIntoView,
  scrollTodoNoteTimetableToItem,
} from '../utils/todoNoteTimetableScroll';
import { supabase } from '../supabaseClient';
import { toISODate } from '../utils/date';
import { normalizeBudgetSearchQuery } from '../utils/budgetNoteSearch';
import {
  buildTodoCategoryNoteSearchMatchId,
  buildTodoItemFieldSearchMatchId,
  buildTodoMemoBodySearchMatchId,
  collectTodoNoteSearchMatches,
  todoItemCalendarPopSearchHit,
} from '../utils/todoNoteSearch';
import {
  formatTodoSharePageTitle,
  isTodoShareDefaultBoard,
  sortTodoShareBoards,
  todoShareNameFromTitle,
} from '../utils/todoShareTitle';
import {
  loadTodoNoteSelectedBoardPreference,
  resolveTodoNoteSelectedBoardId,
  saveTodoNoteSelectedBoardPreference,
} from '../utils/todoNoteSelectedBoardPreference';
import {
  invalidateTodoShareBoardContentCache,
  loadTodoShareBoardContentCache,
  saveTodoShareBoardContentCache,
} from '../utils/todoShareBoardContentCache';
import { matchesMaxWidth, PAGE_ZOOM_CHANGED_EVENT } from '../utils/pageZoom';
import {
  hasTodoPostitLayoutSessionSync,
  loadTodoPostitLayout,
  markTodoPostitLayoutSessionSync,
  parseTodoPostitLayout,
  saveTodoPostitLayout,
  type TodoPostitPosition,
} from '../utils/todoNotePostitLayout';

const DEFAULT_TODO_CATEGORY_COLOR = '#ffffff';
const TODO_CATEGORY_MEMO_DEFAULT_HEIGHT_PX = 216;
const TODO_COMPLETE_HOLD_MS = 2000;
const TODO_COMPLETE_HOLD_DELAY_MS = 500;
const TODO_NOTE_VIEW_MODE_STORAGE_KEY = 'cp.todoNote.viewMode';
const TODO_NOTE_CALENDAR_LAYOUT_KEY = 'cp.todoNote.calendarLayout';
const TODO_NOTE_POSTIT_GROUP_BY_CATEGORY_KEY = 'cp.todoNote.postitGroupByCategory';
/** 사용자 생성 가능 TODO 노트(보드) 최대 개수 */
const MAX_TODO_NOTE_BOARDS = 3;

type TodoNoteCalendarLayoutMode = 'fit' | 'expand';

function readStoredCalendarLayout(): TodoNoteCalendarLayoutMode {
  try {
    const v = window.localStorage.getItem(TODO_NOTE_CALENDAR_LAYOUT_KEY);
    if (v === 'fit' || v === 'expand') return v;
  } catch {
    /* ignore */
  }
  return 'expand';
}

function readStoredPostitGroupByCategory(): boolean {
  try {
    const v = window.localStorage.getItem(TODO_NOTE_POSTIT_GROUP_BY_CATEGORY_KEY);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch {
    /* ignore */
  }
  return true;
}

type TodoNoteViewMode = 'timetable' | 'calendar' | 'postit';

function readStoredTodoNoteViewMode(): TodoNoteViewMode {
  try {
    const raw = window.localStorage.getItem(TODO_NOTE_VIEW_MODE_STORAGE_KEY);
    if (raw === 'timetable' || raw === 'calendar' || raw === 'postit') return raw;
  } catch {
    /* noop */
  }
  return 'timetable';
}

function generateId(): string {
  return crypto.randomUUID();
}

function workspaceSnapshot(
  categories: TodoNoteCategoryDraft[],
  items: TodoNoteItemDraft[],
  deletedCategoryIds: string[],
  deletedItemIds: string[]
): string {
  const c = [...categories].sort((a, b) => a.id.localeCompare(b.id));
  const i = [...items].sort((a, b) => a.id.localeCompare(b.id));
  const dc = [...deletedCategoryIds].sort();
  const di = [...deletedItemIds].sort();
  return JSON.stringify({ c, i, dc, di });
}

function maxBucketOrderIn(items: TodoNoteItemDraft[], bucketKey: string, excludeId?: string): number {
  let m = -1;
  for (const it of items) {
    if (excludeId && it.id === excludeId) continue;
    if (todoItemBucketKey(it) === bucketKey) m = Math.max(m, it.bucket_order);
  }
  return m;
}

function rebalanceBucketOrders(items: TodoNoteItemDraft[], bucketKey: string): TodoNoteItemDraft[] {
  const inBucket = items
    .filter((it) => todoItemBucketKey(it) === bucketKey)
    .sort((a, b) => a.bucket_order - b.bucket_order || a.id.localeCompare(b.id));
  const orderMap = new Map(inBucket.map((it, idx) => [it.id, idx]));
  return items.map((it) => (orderMap.has(it.id) ? { ...it, bucket_order: orderMap.get(it.id)! } : it));
}

function applyDragReorder(
  items: TodoNoteItemDraft[],
  sortedDisplay: TodoNoteItemDraft[],
  activeId: string,
  overId: string
): TodoNoteItemDraft[] {
  const a = sortedDisplay.find((x) => x.id === activeId);
  const b = sortedDisplay.find((x) => x.id === overId);
  if (!a || !b) return items;
  const bk = todoItemBucketKey(a);
  if (bk !== todoItemBucketKey(b)) return items;
  const bucketMembers = sortTodoItemsDrafts(items.filter((it) => todoItemBucketKey(it) === bk));
  const oldIdx = bucketMembers.findIndex((x) => x.id === activeId);
  const newIdx = bucketMembers.findIndex((x) => x.id === overId);
  if (oldIdx < 0 || newIdx < 0) return items;
  const moved = arrayMove(bucketMembers, oldIdx, newIdx);
  const orderMap = new Map(moved.map((it, idx) => [it.id, idx]));
  return items.map((it) => (orderMap.has(it.id) ? { ...it, bucket_order: orderMap.get(it.id)! } : it));
}

function toYmdKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type TodoWorkspaceHistoryState = {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds: string[];
  categoryInputByItemId: Record<string, string>;
};

function historyStateFingerprint(state: TodoWorkspaceHistoryState): string {
  return JSON.stringify({
    ws: workspaceSnapshot(state.categories, state.items, state.deletedCategoryIds, state.deletedItemIds),
    ci: state.categoryInputByItemId,
  });
}

type TodoWorkspaceDraftHydration = {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  categoryInputByItemId: Record<string, string>;
  deletedCategoryIds: string[];
  deletedItemIds: string[];
};

function buildTodoWorkspaceDraftFromSnapshot(
  snap: TodoNoteWorkspaceSnapshot,
  uid: string,
  noteWorkspaceId: string | null
): TodoWorkspaceDraftHydration {
  const existingDefault =
    snap.categories.find((c) => c.name.trim() === DEFAULT_TODO_CATEGORY_NAME) ?? null;
  const ensuredDefault: TodoNoteCategoryDraft = existingDefault
    ? {
        ...existingDefault,
        note_memo: existingDefault.note_memo ?? '',
        workspace_id: existingDefault.workspace_id ?? noteWorkspaceId,
      }
    : ({
        id: generateId(),
        user_id: uid,
        workspace_id: noteWorkspaceId,
        name: DEFAULT_TODO_CATEGORY_NAME,
        color: DEFAULT_TODO_CATEGORY_COLOR,
        sort_order: -1,
        note_memo: '',
      } as TodoNoteCategoryDraft);
  const normalizedSnapCats = snap.categories.map((c) => ({
    ...c,
    workspace_id: c.workspace_id ?? noteWorkspaceId,
    note_memo: c.note_memo ?? '',
  }));
  const ensuredCategories = existingDefault
    ? normalizedSnapCats
    : [ensuredDefault, ...normalizedSnapCats].sort(
        (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
      );
  const ensuredItems = snap.items.map((it) =>
    it.category_id
      ? { ...it, workspace_id: it.workspace_id ?? noteWorkspaceId }
      : { ...it, category_id: ensuredDefault.id, workspace_id: it.workspace_id ?? noteWorkspaceId }
  );
  const merged = mergeDuplicateTodoCategories({
    categories: ensuredCategories,
    items: ensuredItems,
    deletedCategoryIds: [],
  });
  const categoryInputByItemId: Record<string, string> = { ...merged.categoryInputPatches };
  for (const it of merged.items) {
    if (categoryInputByItemId[it.id]) continue;
    const c = merged.categories.find((x) => x.id === it.category_id);
    categoryInputByItemId[it.id] = c?.name ?? DEFAULT_TODO_CATEGORY_NAME;
  }
  return {
    categories: merged.categories,
    items: merged.items,
    categoryInputByItemId,
    deletedCategoryIds: merged.deletedCategoryIds,
    deletedItemIds: merged.deletedItemIds,
  };
}

interface TodoNotePageProps {
  /** 저장·조회에 사용하는 워크스페이스. null이면 계정 전용 노트. */
  noteWorkspaceId: string | null;
  /** 캘린더 뷰의 학사일정·공휴일 조회에 사용(현재 앱에서 보고 있는 워크스페이스). */
  calendarWorkspaceId: string;
  variant?: 'workspace' | 'account';
}

const TodoNotePage: React.FC<TodoNotePageProps> = ({
  noteWorkspaceId,
  calendarWorkspaceId,
  variant = 'workspace',
}) => {
  const { setTodoNoteDirty, saveTodoNoteRef } = useContext(GradeTrackLeaveContext);
  const [boards, setBoards] = useState<TodoShareBoardMeta[]>([]);
  const boardsRef = useRef(boards);
  boardsRef.current = boards;
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(() => {
    try {
      return loadTodoNoteSelectedBoardPreference(noteWorkspaceId)?.id || null;
    } catch {
      return null;
    }
  });
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [boardRevision, setBoardRevision] = useState(1);
  const [postitLayout, setPostitLayout] = useState<Record<string, TodoPostitPosition>>(() => {
    try {
      const id = loadTodoNoteSelectedBoardPreference(noteWorkspaceId)?.id;
      return id ? loadTodoPostitLayout(id) : {};
    } catch {
      return {};
    }
  });
  const [navError, setNavError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');
  const [editBoardId, setEditBoardId] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [boardPickerOpen, setBoardPickerOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const saveInFlightRef = useRef(false);
  const [autoSaveRequest, setAutoSaveRequest] = useState(0);
  const autoSaveHandledRef = useRef(0);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<TodoNoteCategoryDraft[]>([]);
  const [items, setItems] = useState<TodoNoteItemDraft[]>([]);
  const [deletedCategoryIds, setDeletedCategoryIds] = useState<string[]>([]);
  const [deletedItemIds, setDeletedItemIds] = useState<string[]>([]);
  const baselineRef = useRef('');
  /** 서버(보드)에 반영된 것으로 확인된 상태. 저장 시 이것과 달라진 항목만 전송 */
  const serverFingerprintRef = useRef<TodoShareSavedFingerprint | null>(null);
  const savedCategoryIdsRef = useRef<Set<string>>(new Set());
  const savedItemIdsRef = useRef<Set<string>>(new Set());
  const [userId, setUserId] = useState('');
  const lastHydratedBoardRef = useRef('');
  const hydrateGenRef = useRef(0);
  const [filterCategoryId, setFilterCategoryId] = useState<string | null>(null);
  const [isMobileTodoLayout, setIsMobileTodoLayout] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [categoryDeleteModal, setCategoryDeleteModal] = useState<{ id: string; name: string } | null>(null);
  const [categoryInputByItemId, setCategoryInputByItemId] = useState<Record<string, string>>({});
  const [selectedTimetableItemId, setSelectedTimetableItemId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<TodoNoteViewMode>(readStoredTodoNoteViewMode);
  const [calendarLayoutMode, setCalendarLayoutMode] =
    useState<TodoNoteCalendarLayoutMode>(readStoredCalendarLayout);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);
  type ItemAddModalContext =
    | { mode: 'calendar' }
    | { mode: 'timetable'; afterItemId: string | null; duplicate?: boolean };
  const [itemAddModalOpen, setItemAddModalOpen] = useState(false);
  const [itemAddContext, setItemAddContext] = useState<ItemAddModalContext | null>(null);
  const [itemAddDraft, setItemAddDraft] = useState<TodoNoteItemDraft | null>(null);
  const [itemAddCategoryInput, setItemAddCategoryInput] = useState('');
  type ItemAddInitialSnapshot = {
    title: string;
    body: string;
    misc: string;
    category_id: string | null;
    due_kind: TodoNoteItemDraft['due_kind'];
    due_date: string;
    categoryInput: string;
    completed?: boolean;
  };
  const itemAddInitialRef = useRef<ItemAddInitialSnapshot | null>(null);
  const [itemAddDiscardConfirmOpen, setItemAddDiscardConfirmOpen] = useState(false);
  const itemAddDiscardConfirmRef = useRef(false);
  const itemAddModalLiveRef = useRef<{ draft: TodoNoteItemDraft | null; categoryInput: string }>({
    draft: null,
    categoryInput: '',
  });
  const [itemEditModalOpen, setItemEditModalOpen] = useState(false);
  const [itemEditItemId, setItemEditItemId] = useState<string | null>(null);
  const [itemEditOccurrenceYmd, setItemEditOccurrenceYmd] = useState<string | null>(null);
  const [itemEditCompleteMark, setItemEditCompleteMark] = useState<'v' | 'x' | null>(null);
  const [itemEditCompleteTouched, setItemEditCompleteTouched] = useState(false);
  const [itemEditDraft, setItemEditDraft] = useState<TodoNoteItemDraft | null>(null);
  const [itemEditCategoryInput, setItemEditCategoryInput] = useState('');
  const [itemEditDiscardConfirmOpen, setItemEditDiscardConfirmOpen] = useState(false);
  const [routineAddModalOpen, setRoutineAddModalOpen] = useState(false);
  const [routineAddDraft, setRoutineAddDraft] = useState<TodoNoteItemDraft | null>(null);
  const [routineAddCategoryInput, setRoutineAddCategoryInput] = useState('');
  const [routineModalMode, setRoutineModalMode] = useState<'create' | 'edit'>('create');
  const [routineEditOccurrenceYmd, setRoutineEditOccurrenceYmd] = useState<string | null>(null);
  const [routineEditCompleteMark, setRoutineEditCompleteMark] = useState<'v' | 'x' | null>(null);
  const [routineEditCompleteTouched, setRoutineEditCompleteTouched] = useState(false);
  const [routineDeleteConfirmId, setRoutineDeleteConfirmId] = useState<string | null>(null);
  const itemEditInitialRef = useRef<ItemAddInitialSnapshot | null>(null);
  const itemEditDiscardConfirmRef = useRef(false);
  const itemEditModalLiveRef = useRef<{ draft: TodoNoteItemDraft | null; categoryInput: string }>({
    draft: null,
    categoryInput: '',
  });
  const [calendarItemHoldGauge, setCalendarItemHoldGauge] = useState<{ itemId: string; progress: number } | null>(
    null
  );
  const calendarHoldStartRef = useRef<{ itemId: string; startTime: number } | null>(null);
  const calendarHoldRafRef = useRef<number | null>(null);
  const calendarHoldDelayTimerRef = useRef<number | null>(null);
  const calendarHoldCompletedRef = useRef(false);
  const timetableWidthSentinelRef = useRef<HTMLDivElement>(null);
  const timetableHorizInnerRef = useRef<HTMLDivElement>(null);
  const todoNoteScrollBodyRef = useRef<HTMLDivElement>(null);
  const categoryMemoFieldRef = useRef<HTMLTextAreaElement>(null);
  const categoryMemoUserResizedRef = useRef(false);
  const categoryMemoLastSyncedHeightRef = useRef<number | null>(null);
  const categoryMemoApplyingSyncRef = useRef(false);
  const [categoryMemoMinimized, setCategoryMemoMinimized] = useState(false);
  const [holidayDateSet, setHolidayDateSet] = useState<Set<string>>(new Set());
  const [holidayNoteByDate, setHolidayNoteByDate] = useState<Record<string, string>>({});
  const [undoRedoAvail, setUndoRedoAvail] = useState({ canUndo: false, canRedo: false });
  /** ON이면 완료된 블럭을 타임테이블·캘린더 목록에서 숨김 */
  const [hideCompletedTodos, setHideCompletedTodos] = useState(false);
  const [hideRoutineNotes, setHideRoutineNotes] = useState(false);
  const [postitGroupByCategory, setPostitGroupByCategory] = useState(readStoredPostitGroupByCategory);
  const historyRef = useRef<TodoWorkspaceHistoryState[]>([]);
  const historyIndexRef = useRef(-1);
  const suspendHistoryRef = useRef(false);
  const pendingHistoryRef = useRef<TodoWorkspaceHistoryState | null>(null);
  const defaultCategory = useMemo(
    () => categories.find((c) => c.name.trim() === DEFAULT_TODO_CATEGORY_NAME) ?? null,
    [categories]
  );

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? '');
    });
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(TODO_NOTE_VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      /* noop */
    }
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'postit') return;
    setHideCompletedTodos(false);
    setHideRoutineNotes(false);
  }, [viewMode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TODO_NOTE_CALENDAR_LAYOUT_KEY, calendarLayoutMode);
    } catch {
      /* noop */
    }
  }, [calendarLayoutMode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        TODO_NOTE_POSTIT_GROUP_BY_CATEGORY_KEY,
        postitGroupByCategory ? '1' : '0'
      );
    } catch {
      /* noop */
    }
  }, [postitGroupByCategory]);

  useEffect(() => {
    const apply = () => setIsMobileTodoLayout(matchesMaxWidth(900));
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, apply);
    };
  }, []);

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const bootstrapBoards = useCallback(async () => {
    setBoardsLoading(true);
    setNavError('');
    try {
      await migratePersonalTodoToDefaultBoard(noteWorkspaceId);
      const rows = sortTodoShareBoards(await listTodoShareBoards(noteWorkspaceId));
      setBoards(rows);
      // 예전: 항상 rows[0](정렬상 「기본」) 선택. 지금은 저장된 선호만 복원.
      const preferred = loadTodoNoteSelectedBoardPreference(
        noteWorkspaceId,
        userIdRef.current || undefined
      );
      const nextId = resolveTodoNoteSelectedBoardId(rows, preferred);
      setSelectedBoardId(nextId);
      if (preferred && nextId) {
        const matched = rows.find((b) => b.id === nextId);
        if (matched) {
          const matchedName = todoShareNameFromTitle(matched.title ?? '');
          const hitByName = Boolean(preferred.name && matchedName === preferred.name.trim());
          const hitById = Boolean(
            preferred.id &&
              (matched.id === preferred.id.trim() ||
                matched.id.toLowerCase() === preferred.id.trim().toLowerCase())
          );
          if (hitByName || hitById) {
            saveTodoNoteSelectedBoardPreference(noteWorkspaceId, {
              id: matched.id,
              name: matchedName,
            });
          }
        }
      }
    } catch (e) {
      setNavError(e instanceof Error ? e.message : '노트 목록을 불러오지 못했습니다.');
    } finally {
      setBoardsLoading(false);
    }
  }, [noteWorkspaceId]);

  useEffect(() => {
    void bootstrapBoards();
  }, [bootstrapBoards]);

  const persistSelectedBoard = useCallback(
    (board: { id: string; title?: string | null }) => {
      saveTodoNoteSelectedBoardPreference(noteWorkspaceId, {
        id: board.id,
        name: todoShareNameFromTitle(board.title ?? ''),
      });
    },
    [noteWorkspaceId]
  );

  useEffect(() => {
    lastHydratedBoardRef.current = '';
  }, [noteWorkspaceId]);

  const hydrateSelectedBoard = useCallback(
    async (opts?: { quiet?: boolean }): Promise<boolean> => {
      if (!selectedBoardId || !userId) return false;
      const boardId = selectedBoardId;
      const hydrateKey = `${noteWorkspaceId ?? 'account'}:${boardId}`;
      const quiet = Boolean(opts?.quiet);
      const gen = ++hydrateGenRef.current;
      if (quiet) {
        setSyncing(true);
      } else {
        setLoading(true);
        setLoadError(null);
        setFilterCategoryId(null);
      }
      try {
        // 목록(listTodoShareBoards)의 revision과 캐시가 같으면 본문(loadTodoShareAsOwner) 재조회를 생략한다.
        // quiet(=revision 충돌 재동기화)은 항상 서버에서 새로 받는다.
        const knownRevision = quiet
          ? undefined
          : boardsRef.current.find((b) => b.id === boardId)?.revision;
        const cachedContent =
          !quiet && typeof knownRevision === 'number' ? loadTodoShareBoardContentCache(boardId) : null;
        const useCache = Boolean(cachedContent && cachedContent.revision === knownRevision);
        const snap = useCache
          ? {
                board_id: boardId,
                title: cachedContent!.title,
                revision: cachedContent!.revision,
                public_token: cachedContent!.public_token ?? undefined,
                is_active: cachedContent!.is_active ?? undefined,
                categories: cachedContent!.categories,
                items: cachedContent!.items,
                postit_layout: cachedContent!.postit_layout ?? {},
              }
            : await loadTodoShareAsOwner(boardId);
        // 좌표는 본문 revision과 분리. 이 탭에서 한 번 DB에서 받으면(로그아웃 후 재로그인 포함)
        // 탭을 닫기 전까지는 LS·보드 캐시만 쓴다. 드래그 저장이 캐시를 패치한다.
        let remoteLayout = parseTodoPostitLayout(snap.postit_layout);
        let layoutFromDb = !useCache;
        if (useCache && hasTodoPostitLayoutSessionSync(userId, boardId)) {
          if (cachedContent?.postit_layout !== undefined) {
            remoteLayout = parseTodoPostitLayout(cachedContent.postit_layout);
          } else {
            remoteLayout = loadTodoPostitLayout(boardId);
          }
        } else if (useCache) {
          remoteLayout = await loadTodoSharePostitLayout(boardId).catch(() => {
            if (cachedContent?.postit_layout !== undefined) {
              return parseTodoPostitLayout(cachedContent.postit_layout);
            }
            return loadTodoPostitLayout(boardId);
          });
          layoutFromDb = true;
        }
        if (layoutFromDb) markTodoPostitLayoutSessionSync(userId, boardId);
        if (gen !== hydrateGenRef.current) return false;
        lastHydratedBoardRef.current = hydrateKey;
        if (Object.keys(remoteLayout).length > 0) {
          setPostitLayout(remoteLayout);
          saveTodoPostitLayout(boardId, remoteLayout);
        }
        saveTodoShareBoardContentCache(boardId, {
          title: snap.title,
          revision: snap.revision,
          public_token: snap.public_token ?? null,
          is_active: snap.is_active ?? null,
          categories: snap.categories,
          items: snap.items,
          postit_layout: remoteLayout,
        });
        const draftSnap: TodoNoteWorkspaceSnapshot = {
          categories: snap.categories,
          items: snap.items,
        };
        const {
          categories: ensuredCategories,
          items: rawEnsuredItems,
          categoryInputByItemId: ci,
          deletedCategoryIds: dupCategoryIds,
          deletedItemIds: dupItemIds,
        } = buildTodoWorkspaceDraftFromSnapshot(draftSnap, userId, noteWorkspaceId);
        const boardMeta = boardsRef.current.find((b) => b.id === boardId);
        const isDefaultBoard = boardMeta
          ? isTodoShareDefaultBoard(boardMeta)
          : isTodoShareDefaultBoard({ title: snap.title });
        const cachedWorkspace = isDefaultBoard ? loadTodoNoteWorkspaceCache(userId, noteWorkspaceId) : null;
        const bridge = isDefaultBoard ? loadTodoNoteShareBridge(userId, noteWorkspaceId) : null;
        const ensuredItems =
          cachedWorkspace?.found && (!bridge || snap.revision <= bridge.revision)
            ? applyTodoFavoriteFlagsFromSource(rawEnsuredItems, cachedWorkspace.data.items)
            : rawEnsuredItems;
        setBoardRevision(snap.revision);
        setCategories(ensuredCategories);
        setItems(ensuredItems);
        setDeletedCategoryIds(dupCategoryIds);
        setDeletedItemIds(dupItemIds);
        savedCategoryIdsRef.current = new Set([
          ...ensuredCategories.map((c) => c.id),
          ...dupCategoryIds,
        ]);
        savedItemIdsRef.current = new Set([...ensuredItems.map((it) => it.id), ...dupItemIds]);
        setCategoryInputByItemId(ci);
        baselineRef.current = workspaceSnapshot(ensuredCategories, ensuredItems, [], []);
        serverFingerprintRef.current = buildTodoShareSavedFingerprint(snap.categories, snap.items);
        const initialState: TodoWorkspaceHistoryState = {
          categories: ensuredCategories,
          items: ensuredItems,
          deletedCategoryIds: dupCategoryIds,
          deletedItemIds: dupItemIds,
          categoryInputByItemId: ci,
        };
        historyRef.current = [initialState];
        historyIndexRef.current = 0;
        setUndoRedoAvail({ canUndo: false, canRedo: false });
        setBoards((prev) =>
          sortTodoShareBoards(
            prev.map((b) =>
              b.id === boardId
                ? {
                    ...b,
                    title: snap.title,
                    revision: snap.revision,
                    public_token: snap.public_token ?? b.public_token,
                    is_active: snap.is_active ?? b.is_active,
                  }
                : b
            )
          )
        );
        // 기본 보드면 대시보드 keep-alive 캐시에 반영 (추가 fetch 없음)
        if (isDefaultBoard) {
          publishTodoNoteWorkspaceSnapshot(
            userId,
            noteWorkspaceId,
            { categories: ensuredCategories, items: ensuredItems },
            { boardId, revision: snap.revision }
          );
        }
        setLoadError(null);
        return true;
      } catch (e) {
        if (gen !== hydrateGenRef.current) return false;
        const msg = e instanceof Error ? e.message : '불러오기에 실패했습니다.';
        if (quiet) setSaveError(msg);
        else setLoadError(msg);
        return false;
      } finally {
        if (gen === hydrateGenRef.current) {
          if (quiet) setSyncing(false);
          else setLoading(false);
        }
      }
    },
    [selectedBoardId, userId, noteWorkspaceId]
  );

  useEffect(() => {
    if (!selectedBoardId) {
      setPostitLayout({});
      return;
    }
    setPostitLayout(loadTodoPostitLayout(selectedBoardId));
  }, [selectedBoardId]);

  useEffect(() => {
    if (!selectedBoardId || !userId) {
      if (!boardsLoading && !selectedBoardId) {
        setLoading(false);
        setCategories([]);
        setItems([]);
      }
      return;
    }
    void hydrateSelectedBoard();
  }, [selectedBoardId, userId, noteWorkspaceId, boardsLoading, hydrateSelectedBoard]);

  useLayoutEffect(() => {
    itemAddModalLiveRef.current = { draft: itemAddDraft, categoryInput: itemAddCategoryInput };
  }, [itemAddDraft, itemAddCategoryInput]);

  useLayoutEffect(() => {
    itemAddDiscardConfirmRef.current = itemAddDiscardConfirmOpen;
  }, [itemAddDiscardConfirmOpen]);

  useLayoutEffect(() => {
    itemEditModalLiveRef.current = { draft: itemEditDraft, categoryInput: itemEditCategoryInput };
  }, [itemEditDraft, itemEditCategoryInput]);

  useLayoutEffect(() => {
    itemEditDiscardConfirmRef.current = itemEditDiscardConfirmOpen;
  }, [itemEditDiscardConfirmOpen]);

  const sortedItems = useMemo(() => sortTodoItemsDrafts(items), [items]);
  const displayedItems = useMemo(() => {
    const today = toISODate(new Date());
    let list: TodoNoteItemView[] = expandTodoItemsForView(items, today);
    if (filterCategoryId) list = list.filter((it) => it.category_id === filterCategoryId);
    if (hideRoutineNotes) list = list.filter((it) => !it.routineParentId);
    if (hideCompletedTodos) list = list.filter((it) => !it.completed);
    return list;
  }, [items, filterCategoryId, hideCompletedTodos, hideRoutineNotes]);

  const postitItems = useMemo(() => {
    const today = toISODate(new Date());
    let list: TodoNoteItemView[] = expandTodoItemsForView(items, today);
    if (hideRoutineNotes) list = list.filter((it) => !it.routineParentId);
    if (hideCompletedTodos) list = list.filter((it) => !it.completed);
    return list
      .filter(
        (it) =>
          !it.completed &&
          ((it.title ?? '').trim().length > 0 ||
            (it.body ?? '').trim().length > 0 ||
            (it.misc ?? '').trim().length > 0)
      )
      .slice()
      .sort(
        (a, b) =>
          a.due_date.localeCompare(b.due_date) || a.bucket_order - b.bucket_order || a.id.localeCompare(b.id)
      );
  }, [items, hideCompletedTodos, hideRoutineNotes]);

  const todayYmd = toISODate(new Date());

  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const searchMatches = useMemo(
    () =>
      collectTodoNoteSearchMatches({
        query: searchQuery,
        orderedItems: sortedItems,
        categories,
        categoryInputByItemId,
        filterCategoryId,
      }),
    [searchQuery, sortedItems, categories, categoryInputByItemId, filterCategoryId]
  );
  const activeMatch = searchMatches[activeMatchIndex] ?? null;
  const activeMatchId = activeMatch?.matchId ?? null;
  const activeSearchMatchIdRef = useRef<string | null>(null);
  activeSearchMatchIdRef.current = activeMatchId;

  const runTodoSearch = useCallback(() => {
    const q = normalizeBudgetSearchQuery(searchDraft);
    setSearchQuery(q);
    setActiveMatchIndex(0);
  }, [searchDraft]);

  const goPrevTodoSearchMatch = useCallback(() => {
    if (!searchMatches.length) return;
    const currentId = activeSearchMatchIdRef.current;
    const currentIdx = currentId
      ? searchMatches.findIndex((m) => m.matchId === currentId)
      : activeMatchIndex;
    const base = currentIdx >= 0 ? currentIdx : activeMatchIndex;
    setActiveMatchIndex((base - 1 + searchMatches.length) % searchMatches.length);
  }, [searchMatches, activeMatchIndex]);

  const goNextTodoSearchMatch = useCallback(() => {
    if (!searchMatches.length) return;
    const currentId = activeSearchMatchIdRef.current;
    const currentIdx = currentId
      ? searchMatches.findIndex((m) => m.matchId === currentId)
      : activeMatchIndex;
    const base = currentIdx >= 0 ? currentIdx : activeMatchIndex;
    setActiveMatchIndex((base + 1) % searchMatches.length);
  }, [searchMatches, activeMatchIndex]);

  const clearTodoSearch = useCallback(() => {
    setSearchDraft('');
    setSearchQuery('');
    setActiveMatchIndex(0);
  }, []);

  const handleSearchDraftChange = useCallback((next: string) => {
    setSearchDraft(next);
    if (!next) {
      setSearchQuery('');
      setActiveMatchIndex(0);
    }
  }, []);

  const showSearchNavSuffix = Boolean(searchQuery);

  useEffect(() => {
    if (!searchQuery) return;
    if (!searchMatches.length) {
      setActiveMatchIndex(0);
      return;
    }
    const trackedId = activeSearchMatchIdRef.current;
    if (trackedId) {
      const idx = searchMatches.findIndex((m) => m.matchId === trackedId);
      if (idx >= 0) {
        setActiveMatchIndex(idx);
        return;
      }
    }
    setActiveMatchIndex((i) => Math.min(i, searchMatches.length - 1));
  }, [searchQuery, searchMatches]);

  useEffect(() => {
    if (!activeMatch) return;

    if (activeMatch.field === 'category_note') {
      if (activeMatch.categoryId && filterCategoryId !== activeMatch.categoryId) {
        setFilterCategoryId(activeMatch.categoryId);
      }
      if (viewMode !== 'timetable') setViewMode('timetable');
      return;
    }

    const itemId = activeMatch.itemId;
    if (!itemId) return;
    const item = items.find((x) => x.id === itemId);
    if (!item) return;

    if (hideCompletedTodos && item.completed) setHideCompletedTodos(false);
    if (hideRoutineNotes && item.is_routine) setHideRoutineNotes(false);
    if (filterCategoryId && item.category_id !== filterCategoryId) setFilterCategoryId(null);

    if (viewMode === 'calendar') {
      const iso = item.due_date?.trim();
      if (iso) {
        const [ys, ms] = iso.split('-');
        const y = Number(ys);
        const m = Number(ms);
        if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) {
          setCalendarMonth(new Date(y, m - 1, 1));
          setSelectedCalendarDate(iso);
        }
      }
    }
  }, [activeMatch, items, filterCategoryId, hideCompletedTodos, hideRoutineNotes, viewMode]);

  useEffect(() => {
    if (!activeMatchId || !searchQuery) return;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const scrollEl = todoNoteScrollBodyRef.current;
        if (!scrollEl) return;
        const el = scrollEl.querySelector(`[data-budget-search-match="${CSS.escape(activeMatchId)}"]`);
        if (el instanceof HTMLElement) {
          scrollTodoNoteSearchMatchIntoView(scrollEl, el);
          return;
        }
        if (activeMatch?.itemId && viewMode === 'timetable') {
          scrollTodoNoteTimetableToItem(scrollEl, activeMatch.itemId);
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [
    activeMatchId,
    activeMatch,
    searchQuery,
    viewMode,
    filterCategoryId,
    hideCompletedTodos,
    displayedItems.length,
  ]);

  const displayedItemsRef = useRef(displayedItems);
  displayedItemsRef.current = displayedItems;
  /** 같은 보드·필터 컨텍스트에서는 최초 1회만 자동 스크롤 (완료 체크 시 재스크롤 방지) */
  const timetableAutoScrolledKeyRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (loading) {
      // 보드 전환 등 재로드 시 다음 진입에서 다시 자동 스크롤
      timetableAutoScrolledKeyRef.current = null;
      return;
    }
    if (viewMode !== 'timetable' || searchQuery) return;

    const scrollKey = [
      noteWorkspaceId ?? 'account',
      selectedBoardId ?? '',
      filterCategoryId ?? '',
      hideCompletedTodos ? '1' : '0',
      hideRoutineNotes ? '1' : '0',
    ].join(':');

    if (timetableAutoScrolledKeyRef.current === scrollKey) return;
    timetableAutoScrolledKeyRef.current = scrollKey;

    const itemId = findTimetableAutoScrollItemId(displayedItemsRef.current);
    if (!itemId) return;

    let cancelled = false;
    let attempts = 0;
    const timers: number[] = [];

    const tryScroll = () => {
      if (cancelled) return;
      const scrollEl = todoNoteScrollBodyRef.current;
      if (scrollEl && scrollTodoNoteTimetableToItem(scrollEl, itemId)) return;
      attempts += 1;
      if (attempts >= 24) return;
      if (attempts < 12) {
        requestAnimationFrame(tryScroll);
        return;
      }
      timers.push(
        window.setTimeout(() => {
          if (!cancelled) tryScroll();
        }, 32)
      );
    };

    // flex 높이·행 마운트 안정화 후 맞춤. 실패 시 프레임/타임아웃으로 재시도.
    requestAnimationFrame(() => requestAnimationFrame(tryScroll));
    timers.push(window.setTimeout(() => {
      if (!cancelled) tryScroll();
    }, 80));
    timers.push(window.setTimeout(() => {
      if (!cancelled) tryScroll();
    }, 200));

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [
    viewMode,
    loading,
    filterCategoryId,
    hideCompletedTodos,
    hideRoutineNotes,
    noteWorkspaceId,
    selectedBoardId,
    searchQuery,
  ]);

  useEffect(() => {
    if (!selectedTimetableItemId) return;
    const parentId =
      parseRoutineOccurrenceId(selectedTimetableItemId)?.parentId ?? selectedTimetableItemId;
    if (!items.some((it) => it.id === parentId)) {
      setSelectedTimetableItemId(null);
    }
  }, [items, selectedTimetableItemId]);

  useEffect(() => {
    if (!hideCompletedTodos || !selectedTimetableItemId) return;
    const view = displayedItems.find((x) => x.id === selectedTimetableItemId);
    if (view?.completed) setSelectedTimetableItemId(null);
  }, [hideCompletedTodos, displayedItems, selectedTimetableItemId]);

  useEffect(() => {
    if (!hideRoutineNotes || !selectedTimetableItemId) return;
    if (parseRoutineOccurrenceId(selectedTimetableItemId)) {
      setSelectedTimetableItemId(null);
    }
  }, [hideRoutineNotes, selectedTimetableItemId]);

  const dirty = useMemo(() => {
    if (loading) return false;
    return workspaceSnapshot(categories, items, deletedCategoryIds, deletedItemIds) !== baselineRef.current;
  }, [loading, categories, items, deletedCategoryIds, deletedItemIds]);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!userId) return undefined;
    const onTodoSaved = (e: Event) => {
      const d = (e as CustomEvent<TodoNoteWorkspaceSavedDetail>).detail;
      if (!d?.userId || d.userId !== userId || !d.snapshot) return;
      const detailWs = d.noteWorkspaceId?.trim() ? d.noteWorkspaceId.trim() : null;
      const pageWs = noteWorkspaceId?.trim() ? noteWorkspaceId.trim() : null;
      if (detailWs !== pageWs) return;
      const board = boardsRef.current.find((b) => b.id === selectedBoardId);
      if (!board) return;
      const eventBoardId = d.boardId?.trim() || '';
      if (eventBoardId) {
        if (eventBoardId !== selectedBoardId) return;
      } else if (!isTodoShareDefaultBoard(board)) {
        return;
      }

      const bridge = loadTodoNoteShareBridge(userId, noteWorkspaceId);
      if (bridge) {
        setBoardRevision(bridge.revision);
        setBoards((prev) =>
          prev.map((b) => (b.id === bridge.boardId ? { ...b, revision: bridge.revision } : b))
        );
      }

      if (dirtyRef.current) {
        setItems((prev) => applyTodoFavoriteFlagsFromSource(prev, d.snapshot!.items));
        try {
          const prev = JSON.parse(baselineRef.current) as {
            c: unknown;
            i: TodoNoteItemDraft[];
            dc: unknown;
            di: unknown;
          };
          if (Array.isArray(prev.i)) {
            prev.i = applyTodoFavoriteFlagsFromSource(prev.i, d.snapshot!.items);
            baselineRef.current = JSON.stringify(prev);
          }
        } catch {
          /* ignore */
        }
        return;
      }

      suspendHistoryRef.current = true;
      const hydrated = buildTodoWorkspaceDraftFromSnapshot(d.snapshot, userId, noteWorkspaceId);
      const itemsWithFav = applyTodoFavoriteFlagsFromSource(hydrated.items, d.snapshot.items);
      setCategories(hydrated.categories);
      setItems(itemsWithFav);
      setDeletedCategoryIds(hydrated.deletedCategoryIds);
      setDeletedItemIds(hydrated.deletedItemIds);
      savedCategoryIdsRef.current = new Set([
        ...hydrated.categories.map((c) => c.id),
        ...hydrated.deletedCategoryIds,
      ]);
      savedItemIdsRef.current = new Set([...itemsWithFav.map((it) => it.id), ...hydrated.deletedItemIds]);
      setCategoryInputByItemId(hydrated.categoryInputByItemId);
      baselineRef.current = workspaceSnapshot(
        hydrated.categories,
        itemsWithFav,
        hydrated.deletedCategoryIds,
        hydrated.deletedItemIds
      );
      serverFingerprintRef.current = buildTodoShareSavedFingerprint(d.snapshot.categories, d.snapshot.items);
    };
    window.addEventListener(TODO_NOTE_WORKSPACE_SAVED_EVENT, onTodoSaved as EventListener);
    return () => {
      window.removeEventListener(TODO_NOTE_WORKSPACE_SAVED_EVENT, onTodoSaved as EventListener);
    };
  }, [userId, noteWorkspaceId, selectedBoardId]);

  useEffect(() => {
    setTodoNoteDirty(dirty);
  }, [dirty, setTodoNoteDirty]);

  useEffect(() => {
    if (!dirty || loading) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty, loading]);

  const handleSave = useCallback(async (opts?: { background?: boolean }) => {
    if (!selectedBoardId || saveInFlightRef.current) return;
    const background = Boolean(opts?.background);
    saveInFlightRef.current = true;
    setSaveError(null);
    if (background) setAutoSaving(true);
    else setSaving(true);
    try {
      const sentDeletedCategoryIds = new Set(deletedCategoryIds);
      const sentDeletedItemIds = new Set(deletedItemIds);
      const keptCategories = categories.filter(
        (c) => !sentDeletedCategoryIds.has(c.id) || c.id === defaultCategory?.id
      );
      const keptItems = items.filter((it) => !sentDeletedItemIds.has(it.id));
      const delCatIds = deletedCategoryIds.filter((id) => id !== defaultCategory?.id);
      const changed = pickTodoShareChangedForSave(serverFingerprintRef.current, keptCategories, keptItems);
      const newRev = await saveTodoShareAsOwner({
        boardId: selectedBoardId,
        expectedRevision: boardRevision,
        payload: buildTodoShareSavePayload({
          categories: changed.categories,
          items: changed.items,
          deletedCategoryIds: delCatIds,
          deletedItemIds,
        }),
      });
      serverFingerprintRef.current = buildTodoShareSavedFingerprint(keptCategories, keptItems);
      setBoardRevision(newRev);
      // 저장 요청 중에 이어진 편집은 지우지 않고, 이번에 보낸 삭제분만 정리한다.
      setCategories((prev) =>
        prev.filter((c) => !sentDeletedCategoryIds.has(c.id) || c.id === defaultCategory?.id)
      );
      setItems((prev) => prev.filter((it) => !sentDeletedItemIds.has(it.id)));
      setDeletedCategoryIds((prev) => prev.filter((id) => !sentDeletedCategoryIds.has(id)));
      setDeletedItemIds((prev) => prev.filter((id) => !sentDeletedItemIds.has(id)));
      savedCategoryIdsRef.current = new Set(keptCategories.map((c) => c.id));
      savedItemIdsRef.current = new Set(keptItems.map((it) => it.id));
      baselineRef.current = workspaceSnapshot(keptCategories, keptItems, [], []);
      setBoards((prev) =>
        prev.map((b) => (b.id === selectedBoardId ? { ...b, revision: newRev } : b))
      );
      const savedBoard = boards.find((b) => b.id === selectedBoardId);
      saveTodoShareBoardContentCache(selectedBoardId, {
        title: savedBoard?.title ?? '',
        revision: newRev,
        public_token: savedBoard?.public_token ?? null,
        is_active: savedBoard?.is_active ?? null,
        categories: keptCategories,
        items: keptItems,
        postit_layout: postitLayout,
      });
      if (savedBoard ? isTodoShareDefaultBoard(savedBoard) : false) {
        publishTodoNoteWorkspaceSnapshot(
          userId,
          noteWorkspaceId,
          { categories: keptCategories, items: keptItems },
          { boardId: selectedBoardId, revision: newRev }
        );
      } else {
        emitTodoNoteWorkspaceSaved({
          userId,
          noteWorkspaceId,
          snapshot: { categories: keptCategories, items: keptItems },
          boardId: selectedBoardId,
        });
      }
    } catch (e: unknown) {
      if (e instanceof TodoShareRevisionConflictError) {
        const ok = await hydrateSelectedBoard({ quiet: true });
        if (ok) {
          setSaveError(
            '다른 사람이 먼저 저장해 서버 버전으로 맞췄습니다. 필요하면 다시 수정한 뒤 저장해 주세요.'
          );
        }
      } else {
        setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.');
      }
    } finally {
      saveInFlightRef.current = false;
      if (background) setAutoSaving(false);
      else setSaving(false);
    }
  }, [
    selectedBoardId,
    boardRevision,
    categories,
    items,
    deletedCategoryIds,
    deletedItemIds,
    defaultCategory,
    hydrateSelectedBoard,
    boards,
    userId,
    noteWorkspaceId,
    postitLayout,
  ]);

  /** 블럭 이탈·모달 확인 등 편집이 끝난 시점에 호출. 상태 반영 후 변경분만 백그라운드 저장 */
  const requestAutoSave = useCallback(() => {
    setAutoSaveRequest((n) => n + 1);
  }, []);

  useEffect(() => {
    if (autoSaveRequest === autoSaveHandledRef.current) return;
    if (loading || saving || autoSaving || saveInFlightRef.current) return;
    autoSaveHandledRef.current = autoSaveRequest;
    if (!dirty) return;
    void handleSave({ background: true });
  }, [autoSaveRequest, loading, saving, autoSaving, dirty, handleSave]);

  const pushHistorySnapshot = useCallback((next: TodoWorkspaceHistoryState) => {
    const idx = historyIndexRef.current;
    const current = historyRef.current[idx];
    if (current && historyStateFingerprint(current) === historyStateFingerprint(next)) return;
    const base = historyRef.current.slice(0, idx + 1);
    base.push(next);
    historyRef.current = base.slice(-150);
    historyIndexRef.current = historyRef.current.length - 1;
    setUndoRedoAvail({
      canUndo: historyIndexRef.current > 0,
      canRedo: false,
    });
  }, []);

  const isTypingFieldActive = () => {
    const active = document.activeElement;
    if (!active) return false;
    if (!(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)) return false;
    return Boolean(active.closest('.cp-todo-note-page'));
  };

  const flushPendingHistory = useCallback(() => {
    if (loading || suspendHistoryRef.current) return;
    const pending = pendingHistoryRef.current;
    if (!pending) return;
    if (isTypingFieldActive()) return;
    pendingHistoryRef.current = null;
    pushHistorySnapshot(pending);
  }, [loading, pushHistorySnapshot]);

  useEffect(() => {
    if (loading) return;
    if (suspendHistoryRef.current) {
      suspendHistoryRef.current = false;
      return;
    }
    const next: TodoWorkspaceHistoryState = {
      categories,
      items,
      deletedCategoryIds,
      deletedItemIds,
      categoryInputByItemId,
    };
    if (isTypingFieldActive()) {
      pendingHistoryRef.current = next;
      return;
    }
    pushHistorySnapshot(next);
  }, [loading, categories, items, deletedCategoryIds, deletedItemIds, categoryInputByItemId, pushHistorySnapshot]);

  const applyHistoryState = (state: TodoWorkspaceHistoryState) => {
    suspendHistoryRef.current = true;
    setCategories(state.categories);
    setItems(state.items);
    setDeletedCategoryIds(state.deletedCategoryIds);
    setDeletedItemIds(state.deletedItemIds);
    setCategoryInputByItemId(state.categoryInputByItemId);
  };

  const handleUndo = () => {
    if (historyIndexRef.current <= 0) return;
    const nextIdx = historyIndexRef.current - 1;
    const next = historyRef.current[nextIdx];
    if (!next) return;
    historyIndexRef.current = nextIdx;
    setUndoRedoAvail({
      canUndo: nextIdx > 0,
      canRedo: nextIdx < historyRef.current.length - 1,
    });
    applyHistoryState(next);
  };

  const handleRedo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    const nextIdx = historyIndexRef.current + 1;
    const next = historyRef.current[nextIdx];
    if (!next) return;
    historyIndexRef.current = nextIdx;
    setUndoRedoAvail({
      canUndo: nextIdx > 0,
      canRedo: nextIdx < historyRef.current.length - 1,
    });
    applyHistoryState(next);
  };

  useEffect(() => {
    saveTodoNoteRef.current = { save: handleSave };
    return () => {
      saveTodoNoteRef.current = null;
    };
  }, [handleSave, saveTodoNoteRef]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const categoryDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const onDragEnd = (e: DragEndEvent) => {
    if (filterCategoryId !== null) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    if (parseRoutineOccurrenceId(String(active.id)) || parseRoutineOccurrenceId(String(over.id))) return;
    setItems((prev) => applyDragReorder(prev, displayedItems, String(active.id), String(over.id)));
  };

  const handleCategoryDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setCategories((prev) => {
      const sorted = [...prev].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
      const oldIndex = sorted.findIndex((c) => c.id === String(active.id));
      const newIndex = sorted.findIndex((c) => c.id === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(sorted, oldIndex, newIndex).map((c, i) => ({ ...c, sort_order: i }));
    });
  };

  const closeItemAddModal = useCallback(() => {
    setItemAddModalOpen(false);
    setItemAddContext(null);
    setItemAddDraft(null);
    setItemAddCategoryInput('');
    setItemAddDiscardConfirmOpen(false);
    itemAddInitialRef.current = null;
  }, []);

  const tryDismissItemAddModal = useCallback(() => {
    if (itemAddDiscardConfirmRef.current) {
      setItemAddDiscardConfirmOpen(false);
      return;
    }
    const init = itemAddInitialRef.current;
    const { draft, categoryInput } = itemAddModalLiveRef.current;
    if (!init || !draft) {
      closeItemAddModal();
      return;
    }
    const isDirty =
      draft.title.trim() !== init.title.trim() ||
      draft.body.trim() !== init.body.trim() ||
      draft.misc.trim() !== init.misc.trim() ||
      draft.category_id !== init.category_id ||
      draft.due_kind !== init.due_kind ||
      draft.due_date !== init.due_date ||
      categoryInput.trim() !== init.categoryInput.trim();
    if (isDirty) {
      setItemAddDiscardConfirmOpen(true);
      return;
    }
    closeItemAddModal();
  }, [closeItemAddModal]);

  const openItemAddModal = useCallback(
    (context: ItemAddModalContext) => {
      if (!userId) return;
      const today = toISODate(new Date());
      const selectedFilterCategory =
        filterCategoryId != null ? categories.find((c) => c.id === filterCategoryId) ?? null : null;
      const baseCategory = selectedFilterCategory ?? defaultCategory ?? null;
      const baseCategoryId = baseCategory?.id ?? null;
      let dueKind: TodoNoteItemDraft['due_kind'] = 'day';
      let dueDate = today;
      let afterItemId: string | null = null;

      if (context.mode === 'calendar') {
        dueDate = selectedCalendarDate ?? toYmdKey(new Date());
      } else {
        const selected = context.afterItemId
          ? items.find((it) => it.id === context.afterItemId) ?? null
          : null;
        afterItemId = selected?.id ?? null;
        if (selected) {
          dueKind = selected.due_kind;
          dueDate = selected.due_date;
        }
      }

      const draftId = `__add-draft-${generateId()}`;
      setItemAddDiscardConfirmOpen(false);
      setItemAddDraft({
        id: draftId,
        user_id: userId,
        workspace_id: noteWorkspaceId,
        category_id: baseCategoryId,
        due_kind: dueKind,
        due_date: dueDate,
        title: '',
        body: '',
        misc: '',
        completed: false,
        bucket_order: 0,
      });
      setItemAddCategoryInput(baseCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME);
      itemAddInitialRef.current = {
        title: '',
        body: '',
        misc: '',
        category_id: baseCategoryId,
        due_kind: dueKind,
        due_date: dueDate,
        categoryInput: baseCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME,
      };
      setItemAddContext(
        context.mode === 'calendar' ? { mode: 'calendar' } : { mode: 'timetable', afterItemId }
      );
      setItemAddModalOpen(true);
    },
    [categories, defaultCategory, filterCategoryId, items, selectedCalendarDate, userId, noteWorkspaceId]
  );

  const patchItemAddDraft = useCallback((patch: Partial<TodoNoteItemDraft>) => {
    setItemAddDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const submitItemAddModal = useCallback(() => {
    if (!userId || !itemAddDraft || !itemAddContext) return;
    const newId = generateId();
    const categoryId = itemAddDraft.category_id ?? defaultCategory?.id ?? null;
    const finalItem: TodoNoteItemDraft = {
      ...itemAddDraft,
      id: newId,
      user_id: userId,
      workspace_id: noteWorkspaceId,
      category_id: categoryId,
      completed: !!itemAddDraft.completed,
    };

    setItems((prev) => {
      if (itemAddContext.mode === 'timetable' && itemAddContext.afterItemId) {
        const anchor = prev.find((it) => it.id === itemAddContext.afterItemId);
        if (anchor) {
          const next = [...prev, { ...finalItem, bucket_order: anchor.bucket_order + 0.5 }];
          return rebalanceBucketOrders(next, todoItemBucketKey(finalItem));
        }
      }
      const draft = { ...finalItem, bucket_order: 0 };
      const bk = todoItemBucketKey(draft);
      const bo = maxBucketOrderIn(prev, bk) + 1;
      return [...prev, { ...draft, bucket_order: bo }];
    });

    const catName = categoryId ? categories.find((c) => c.id === categoryId)?.name ?? '' : '';
    setCategoryInputByItemId((m) => ({ ...m, [newId]: catName }));
    if (itemAddContext.mode === 'timetable') {
      setSelectedTimetableItemId(newId);
    }
    closeItemAddModal();
    if (viewMode !== 'timetable') requestAutoSave();
  }, [
    viewMode,
    categories,
    closeItemAddModal,
    defaultCategory?.id,
    itemAddContext,
    itemAddDraft,
    userId,
    noteWorkspaceId,
    requestAutoSave,
  ]);

  const openDuplicateItemModal = useCallback(
    (sourceId: string) => {
      if (!userId) return;
      if (parseRoutineOccurrenceId(sourceId)) return;
      const anchor = items.find((x) => x.id === sourceId);
      if (!anchor || anchor.is_routine) return;
      const categoryLabel = anchor.category_id
        ? categories.find((c) => c.id === anchor.category_id)?.name ?? DEFAULT_TODO_CATEGORY_NAME
        : DEFAULT_TODO_CATEGORY_NAME;
      const draftId = `__add-draft-${generateId()}`;
      setItemAddDiscardConfirmOpen(false);
      setItemAddDraft({
        ...anchor,
        id: draftId,
        user_id: userId,
        workspace_id: noteWorkspaceId,
        completed: false,
        is_favorite: false,
      });
      setItemAddCategoryInput(categoryLabel);
      itemAddInitialRef.current = {
        title: anchor.title,
        body: anchor.body,
        misc: anchor.misc,
        category_id: anchor.category_id,
        due_kind: anchor.due_kind,
        due_date: anchor.due_date,
        categoryInput: categoryLabel,
      };
      setItemAddContext({ mode: 'timetable', afterItemId: sourceId, duplicate: true });
      setItemAddModalOpen(true);
    },
    [categories, items, noteWorkspaceId, userId]
  );

  const openCalendarAddModal = useCallback(() => {
    openItemAddModal({ mode: 'calendar' });
  }, [openItemAddModal]);

  const openTimetableAddModal = useCallback(() => {
    openItemAddModal({ mode: 'timetable', afterItemId: selectedTimetableItemId });
  }, [openItemAddModal, selectedTimetableItemId]);

  const closeItemEditModal = useCallback(() => {
    setItemEditModalOpen(false);
    setItemEditItemId(null);
    setItemEditOccurrenceYmd(null);
    setItemEditCompleteMark(null);
    setItemEditCompleteTouched(false);
    setItemEditDraft(null);
    setItemEditCategoryInput('');
    setItemEditDiscardConfirmOpen(false);
    itemEditInitialRef.current = null;
  }, []);

  const tryDismissItemEditModal = useCallback(() => {
    if (itemEditDiscardConfirmRef.current) {
      setItemEditDiscardConfirmOpen(false);
      return;
    }
    const init = itemEditInitialRef.current;
    const { draft, categoryInput } = itemEditModalLiveRef.current;
    if (!init || !draft) {
      closeItemEditModal();
      return;
    }
    const isDirty =
      draft.title.trim() !== init.title.trim() ||
      draft.body.trim() !== init.body.trim() ||
      draft.misc.trim() !== init.misc.trim() ||
      draft.category_id !== init.category_id ||
      draft.due_kind !== init.due_kind ||
      draft.due_date !== init.due_date ||
      draft.completed !== init.completed ||
      categoryInput.trim() !== init.categoryInput.trim();
    if (isDirty) {
      setItemEditDiscardConfirmOpen(true);
      return;
    }
    closeItemEditModal();
  }, [closeItemEditModal]);

  const openCalendarItemEditModal = useCallback(
    (itemId: string) => {
      const occ = parseRoutineOccurrenceId(itemId);
      const parentId = occ?.parentId ?? itemId;
      const item = items.find((it) => it.id === parentId);
      if (!item) return;
      const view = displayedItems.find((it) => it.id === itemId);
      const cat = item.category_id ? categories.find((c) => c.id === item.category_id) : null;
      const catInput =
        cat?.name ?? categoryInputByItemId[parentId] ?? defaultCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME;
      if (item.is_routine && item.routine) {
        const ymd = occ?.ymd ?? null;
        setRoutineAddDraft({
          ...item,
          due_kind: 'day',
          due_date: ymd ?? item.due_date,
          completed: view?.completed ?? false,
        });
        setRoutineAddCategoryInput(catInput);
        setRoutineEditOccurrenceYmd(ymd);
        setRoutineEditCompleteMark(view?.completeMark ?? (view?.completed ? 'v' : null));
        setRoutineEditCompleteTouched(false);
        setRoutineModalMode('edit');
        setRoutineAddModalOpen(true);
        return;
      }
      const ymd = occ?.ymd ?? null;
      setItemEditDiscardConfirmOpen(false);
      setItemEditItemId(parentId);
      setItemEditOccurrenceYmd(ymd);
      setItemEditCompleteMark(view?.completeMark ?? (view?.completed ? 'v' : null));
      setItemEditCompleteTouched(false);
      setItemEditDraft({
        ...item,
        due_kind: 'day',
        due_date: ymd ?? item.due_date,
        completed: view?.completed ?? item.completed,
      });
      setItemEditCategoryInput(catInput);
      itemEditInitialRef.current = {
        title: item.title,
        body: item.body,
        misc: item.misc,
        category_id: item.category_id,
        due_kind: item.due_kind,
        due_date: ymd ?? item.due_date,
        categoryInput: catInput,
        completed: view?.completed ?? item.completed,
      };
      setItemEditModalOpen(true);
    },
    [items, categories, categoryInputByItemId, defaultCategory?.name, displayedItems]
  );

  const patchItemEditDraft = useCallback((patch: Partial<TodoNoteItemDraft>) => {
    setItemEditDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  useEffect(() => {
    if (!itemEditItemId) return;
    if (!items.some((it) => it.id === itemEditItemId)) {
      closeItemEditModal();
    }
  }, [items, itemEditItemId, closeItemEditModal]);

  useEffect(() => {
    if (viewMode !== 'calendar' && viewMode !== 'postit' && itemEditModalOpen) {
      closeItemEditModal();
    }
  }, [viewMode, itemEditModalOpen, closeItemEditModal]);

  const addCategoryFromModal = (payload: { name: string; color: string; categoryId?: string | null }) => {
    if (!userId) return;
    const next = applyTodoCategoryModalConfirm({
      categories,
      items,
      deletedCategoryIds,
      deletedItemIds,
      payload: {
        name: payload.name,
        color: payload.color,
        categoryId: payload.categoryId ?? editingCategoryId,
      },
      defaultCategoryName: DEFAULT_TODO_CATEGORY_NAME,
      userId,
      workspaceId: noteWorkspaceId,
      generateId,
    });
    if (!next) return;
    setCategories(next.categories);
    setItems(next.items);
    setDeletedCategoryIds(next.deletedCategoryIds);
    setDeletedItemIds(next.deletedItemIds);
    if (Object.keys(next.categoryInputPatches).length > 0) {
      setCategoryInputByItemId((prev) => ({ ...prev, ...next.categoryInputPatches }));
    }
    if (filterCategoryId && !next.categories.some((c) => c.id === filterCategoryId)) {
      setFilterCategoryId(next.categories[0]?.id ?? null);
    }
  };

  const startEditCategory = (id: string) => {
    if (!id) return;
    setEditingCategoryId(id);
    setCategoryModalOpen(true);
  };

  const removeCategory = (id: string) => {
    if (!id || defaultCategory?.id === id) return;
    const fallbackId = defaultCategory?.id ?? null;
    setCategories((prev) => prev.filter((c) => c.id !== id));
    setItems((prev) => prev.map((it) => (it.category_id === id ? { ...it, category_id: fallbackId } : it)));
    setCategoryInputByItemId((prev) => {
      const next = { ...prev };
      for (const it of items) {
        if (it.category_id === id) next[it.id] = defaultCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME;
      }
      return next;
    });
    if (savedCategoryIdsRef.current.has(id)) {
      setDeletedCategoryIds((d) => (d.includes(id) ? d : [...d, id]));
    }
    if (filterCategoryId === id) setFilterCategoryId(null);
  };

  const removeItemFixed = (id: string) => {
    const parentId = parseRoutineOccurrenceId(id)?.parentId ?? id;
    setItems((prev) => prev.filter((x) => x.id !== parentId));
    setCategoryInputByItemId((m) => {
      const n = { ...m };
      delete n[parentId];
      delete n[id];
      return n;
    });
    if (savedItemIdsRef.current.has(parentId)) {
      setDeletedItemIds((d) => (d.includes(parentId) ? d : [...d, parentId]));
    }
    if (
      selectedTimetableItemId === id ||
      selectedTimetableItemId === parentId ||
      (selectedTimetableItemId && parseRoutineOccurrenceId(selectedTimetableItemId)?.parentId === parentId)
    ) {
      setSelectedTimetableItemId(null);
    }
  };

  const toggleItemFavorite = (viewId: string) => {
    const persistId = parseRoutineOccurrenceId(viewId)?.parentId ?? viewId;
    const item = items.find((x) => x.id === persistId);
    if (!item) return;
    const nextFav = !item.is_favorite;
    const updatedItems = items.map((it) => (it.id === persistId ? { ...it, is_favorite: nextFav } : it));
    setItems(updatedItems);
    try {
      const prev = JSON.parse(baselineRef.current) as {
        c: unknown;
        i: TodoNoteItemDraft[];
        dc: unknown;
        di: unknown;
      };
      if (Array.isArray(prev.i)) {
        prev.i = prev.i.map((it) => (it.id === persistId ? { ...it, is_favorite: nextFav } : it));
        baselineRef.current = JSON.stringify(prev);
      }
    } catch {
      /* ignore */
    }

    const board = boardsRef.current.find((b) => b.id === selectedBoardId);
    if (!board || !userId) return;
    void (async () => {
      try {
        if (isTodoShareDefaultBoard(board)) {
          const cached = loadTodoNoteWorkspaceCache(userId, noteWorkspaceId);
          const persistItems = applyTodoFavoriteFlagsFromSource(
            cached.found ? cached.data.items : updatedItems,
            [{ id: persistId, is_favorite: nextFav }]
          );
          await saveTodoNoteWorkspace({
            noteWorkspaceId,
            categories: cached.found ? cached.data.categories : categories,
            items: persistItems,
            deletedCategoryIds: [],
            deletedItemIds: [],
          });
          const bridge = loadTodoNoteShareBridge(userId, noteWorkspaceId);
          if (bridge) {
            setBoardRevision(bridge.revision);
            setBoards((prev) =>
              prev.map((b) => (b.id === bridge.boardId ? { ...b, revision: bridge.revision } : b))
            );
          }
          return;
        }
        if (!selectedBoardId) return;
        const patched = updatedItems.find((it) => it.id === persistId);
        if (!patched) return;
        const newRev = await saveTodoShareAsOwner({
          boardId: selectedBoardId,
          expectedRevision: boardRevision,
          payload: buildTodoShareSavePayload({
            categories: [],
            items: [patched],
            deletedCategoryIds: [],
            deletedItemIds: [],
          }),
        });
        setBoardRevision(newRev);
        setBoards((prev) =>
          prev.map((b) => (b.id === selectedBoardId ? { ...b, revision: newRev } : b))
        );
        const cached = loadTodoShareBoardContentCache(selectedBoardId);
        if (cached) {
          saveTodoShareBoardContentCache(selectedBoardId, {
            ...cached,
            revision: newRev,
            items: applyTodoFavoriteFlagsFromSource(cached.items, [patched]),
          });
        }
      } catch {
        /* 로컬 별은 유지. 다음 저장에서 재시도 */
      }
    })();
  };

  const requestRemoveItem = (viewId: string) => {
    const parentId = parseRoutineOccurrenceId(viewId)?.parentId ?? viewId;
    const parent = items.find((x) => x.id === parentId);
    if (parent?.is_routine) {
      setRoutineDeleteConfirmId(parentId);
      return;
    }
    removeItemFixed(viewId);
  };

  const openRoutineEdit = (parentId: string) => {
    const parent = items.find((x) => x.id === parentId);
    if (!parent?.is_routine || !parent.routine) return;
    setRoutineAddDraft({ ...parent, completed: false });
    setRoutineAddCategoryInput(
      parent.category_id
        ? categories.find((c) => c.id === parent.category_id)?.name ??
            categoryInputByItemId[parentId] ??
            ''
        : categoryInputByItemId[parentId] ?? ''
    );
    setRoutineEditOccurrenceYmd(null);
    setRoutineEditCompleteMark(null);
    setRoutineEditCompleteTouched(false);
    setRoutineModalMode('edit');
    setRoutineAddModalOpen(true);
  };

  const closeRoutineModal = () => {
    setRoutineAddModalOpen(false);
    setRoutineAddDraft(null);
    setRoutineModalMode('create');
    setRoutineEditOccurrenceYmd(null);
    setRoutineEditCompleteMark(null);
    setRoutineEditCompleteTouched(false);
  };

  const patchViewItemFields = (viewId: string, patch: Partial<TodoNoteItemDraft>) => {
    const occ = parseRoutineOccurrenceId(viewId);
    const parentId = occ?.parentId ?? viewId;
    if (occ && (patch.due_date !== undefined || patch.due_kind !== undefined)) return;
    patchItem(parentId, patch);
  };

  const toggleViewComplete = (viewId: string) => {
    const occ = parseRoutineOccurrenceId(viewId);
    if (occ) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === occ.parentId ? toggleRoutineOccurrenceComplete(it, occ.ymd, todayYmd) : it
        )
      );
      return;
    }
    const item = items.find((it) => it.id === viewId);
    if (item) patchItem(viewId, { completed: !item.completed });
  };

  const duplicateItemBelow = useCallback(
    (sourceId: string): string | null => {
      if (!userId) return null;
      if (parseRoutineOccurrenceId(sourceId)) return null;
      const anchor = items.find((x) => x.id === sourceId);
      if (!anchor || anchor.is_routine) return null;
      const newId = generateId();
      const categoryLabel = anchor.category_id
        ? categories.find((c) => c.id === anchor.category_id)?.name ?? ''
        : '';
      setItems((prev) => {
        const a = prev.find((x) => x.id === sourceId);
        if (!a) return prev;
        const dup: TodoNoteItemDraft = {
          ...a,
          id: newId,
          user_id: userId,
          completed: false,
          is_favorite: false,
          bucket_order: a.bucket_order + 0.5,
        };
        return rebalanceBucketOrders([...prev, dup], todoItemBucketKey(dup));
      });
      setCategoryInputByItemId((m) => ({ ...m, [newId]: categoryLabel }));
      setSelectedTimetableItemId(newId);
      return newId;
    },
    [userId, items, categories]
  );

  const patchItem = (id: string, patch: Partial<TodoNoteItemDraft>) => {
    setItems((prev) => {
      const cur = prev.find((x) => x.id === id);
      if (!cur) return prev;
      const next = { ...cur, ...patch } as TodoNoteItemDraft;
      if (patch.due_kind !== undefined || patch.due_date !== undefined) {
        const oldKey = todoItemBucketKey(cur);
        const newKey = todoItemBucketKey(next);
        if (oldKey !== newKey) {
          const bo = maxBucketOrderIn(prev, newKey, id) + 1;
          const merged = { ...next, bucket_order: bo };
          let out = prev.map((x) => (x.id === id ? merged : x));
          out = rebalanceBucketOrders(out, oldKey);
          out = rebalanceBucketOrders(out, newKey);
          return out;
        }
      }
      return prev.map((x) => (x.id === id ? next : x));
    });
  };

  const submitItemEditModal = useCallback(() => {
    if (!itemEditDraft || !itemEditItemId) return;
    const categoryId = itemEditDraft.category_id ?? defaultCategory?.id ?? null;
    if (itemEditOccurrenceYmd) {
      setItems((prev) =>
        prev.map((x) => {
          if (x.id !== itemEditItemId) return x;
          const base: TodoNoteItemDraft = {
            ...x,
            category_id: categoryId,
            title: itemEditDraft.title,
            body: itemEditDraft.body,
            misc: itemEditDraft.misc,
            completed: false,
            is_routine: true,
          };
          if (!itemEditCompleteTouched) return base;
          return patchRoutineDayLog(base, itemEditOccurrenceYmd, {
            completedOverride: itemEditDraft.completed,
          });
        })
      );
    } else {
      patchItem(itemEditItemId, {
        category_id: categoryId,
        due_kind: itemEditDraft.due_kind,
        due_date: itemEditDraft.due_date,
        title: itemEditDraft.title,
        body: itemEditDraft.body,
        misc: itemEditDraft.misc,
        completed: itemEditDraft.completed,
      });
    }
    const catName = categoryId ? categories.find((c) => c.id === categoryId)?.name ?? '' : '';
    setCategoryInputByItemId((m) => ({ ...m, [itemEditItemId]: catName }));
    closeItemEditModal();
    if (viewMode !== 'timetable') requestAutoSave();
  }, [
    viewMode,
    categories,
    closeItemEditModal,
    defaultCategory?.id,
    itemEditDraft,
    itemEditItemId,
    itemEditOccurrenceYmd,
    itemEditCompleteTouched,
    requestAutoSave,
  ]);

  const cancelCalendarItemHoldGauge = useCallback(() => {
    if (calendarHoldDelayTimerRef.current != null) {
      window.clearTimeout(calendarHoldDelayTimerRef.current);
      calendarHoldDelayTimerRef.current = null;
    }
    if (calendarHoldRafRef.current != null) {
      cancelAnimationFrame(calendarHoldRafRef.current);
      calendarHoldRafRef.current = null;
    }
    calendarHoldStartRef.current = null;
    setCalendarItemHoldGauge(null);
  }, []);

  const runCalendarItemHoldGaugeLoop = useCallback(
    (itemId: string) => {
      const loop = () => {
        if (!calendarHoldStartRef.current || calendarHoldStartRef.current.itemId !== itemId) return;
        const elapsed = performance.now() - calendarHoldStartRef.current.startTime;
        const progress = Math.min(1, elapsed / TODO_COMPLETE_HOLD_MS);
        setCalendarItemHoldGauge({ itemId, progress });
        if (progress >= 1) {
          calendarHoldStartRef.current = null;
          setCalendarItemHoldGauge(null);
          calendarHoldCompletedRef.current = true;
          const occ = parseRoutineOccurrenceId(itemId);
          if (occ) {
            setItems((prev) =>
              prev.map((it) =>
                it.id === occ.parentId ? toggleRoutineOccurrenceComplete(it, occ.ymd, todayYmd) : it
              )
            );
          } else {
            const item = items.find((it) => it.id === itemId);
            if (item) {
              patchItem(itemId, { completed: !item.completed });
            }
          }
          return;
        }
        calendarHoldRafRef.current = requestAnimationFrame(loop);
      };
      calendarHoldRafRef.current = requestAnimationFrame(loop);
    },
    [items, todayYmd]
  );

  const scheduleCalendarItemHoldGauge = useCallback(
    (itemId: string) => {
      cancelCalendarItemHoldGauge();
      calendarHoldCompletedRef.current = false;
      calendarHoldDelayTimerRef.current = window.setTimeout(() => {
        calendarHoldDelayTimerRef.current = null;
        calendarHoldStartRef.current = { itemId, startTime: performance.now() };
        runCalendarItemHoldGaugeLoop(itemId);
      }, TODO_COMPLETE_HOLD_DELAY_MS);
    },
    [cancelCalendarItemHoldGauge, runCalendarItemHoldGaugeLoop]
  );

  const releaseCalendarItemPointer = useCallback(
    (itemId: string, el: HTMLDivElement, pointerId: number) => {
      if (el.hasPointerCapture(pointerId)) {
        try {
          el.releasePointerCapture(pointerId);
        } catch {
          /* noop */
        }
      }
      const didComplete = calendarHoldCompletedRef.current;
      cancelCalendarItemHoldGauge();
      if (!didComplete) {
        openCalendarItemEditModal(itemId);
      }
    },
    [cancelCalendarItemHoldGauge, openCalendarItemEditModal]
  );

  useEffect(() => () => cancelCalendarItemHoldGauge(), [cancelCalendarItemHoldGauge]);

  useEffect(() => {
    if (viewMode !== 'calendar') {
      cancelCalendarItemHoldGauge();
    }
  }, [viewMode, cancelCalendarItemHoldGauge]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [categories]
  );

  const categoryMap = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const filterCategory = useMemo(
    () => (filterCategoryId ? categories.find((c) => c.id === filterCategoryId) ?? null : null),
    [categories, filterCategoryId]
  );

  useEffect(() => {
    setCategoryMemoMinimized(false);
    categoryMemoUserResizedRef.current = false;
    categoryMemoLastSyncedHeightRef.current = null;
  }, [filterCategoryId]);

  const updateCategoryMemoMaxHeight = useCallback(() => {
    const field = categoryMemoFieldRef.current;
    const scrollBody = todoNoteScrollBodyRef.current;
    if (!field) return;

    const scrollBottom = scrollBody?.getBoundingClientRect().bottom ?? window.innerHeight;
    const fieldTop = field.getBoundingClientRect().top;
    const maxH = Math.max(
      TODO_CATEGORY_MEMO_DEFAULT_HEIGHT_PX,
      Math.floor(scrollBottom - fieldTop - 12)
    );

    if (categoryMemoMinimized) {
      field.style.maxHeight = `${TODO_CATEGORY_MEMO_DEFAULT_HEIGHT_PX}px`;
      return;
    }
    field.style.maxHeight = `${maxH}px`;
  }, [categoryMemoMinimized]);

  const fitCategoryMemoToContent = useCallback(() => {
    const field = categoryMemoFieldRef.current;
    if (!field || categoryMemoMinimized) return;

    updateCategoryMemoMaxHeight();
    categoryMemoApplyingSyncRef.current = true;
    field.style.height = 'auto';
    const contentH = field.scrollHeight;
    const maxH = Number.parseInt(field.style.maxHeight, 10) || contentH;
    const nextH = Math.max(
      TODO_CATEGORY_MEMO_DEFAULT_HEIGHT_PX,
      Math.min(maxH, contentH)
    );
    field.style.height = `${nextH}px`;
    categoryMemoLastSyncedHeightRef.current = nextH;
    categoryMemoApplyingSyncRef.current = false;
  }, [categoryMemoMinimized, updateCategoryMemoMaxHeight]);

  const applyCategoryMemoMinimizedHeight = useCallback(() => {
    const field = categoryMemoFieldRef.current;
    if (!field) return;

    categoryMemoApplyingSyncRef.current = true;
    field.style.height = `${TODO_CATEGORY_MEMO_DEFAULT_HEIGHT_PX}px`;
    field.style.maxHeight = `${TODO_CATEGORY_MEMO_DEFAULT_HEIGHT_PX}px`;
    categoryMemoLastSyncedHeightRef.current = TODO_CATEGORY_MEMO_DEFAULT_HEIGHT_PX;
    categoryMemoApplyingSyncRef.current = false;
  }, []);

  /** 카테고리 메모: 처음 열 때·펼칠 때 내용 높이 자동, 이후 수동 리사이즈 유지 */
  useLayoutEffect(() => {
    if (viewMode !== 'timetable' || !filterCategoryId) return;
    updateCategoryMemoMaxHeight();
    if (categoryMemoMinimized) {
      applyCategoryMemoMinimizedHeight();
    } else if (!categoryMemoUserResizedRef.current) {
      fitCategoryMemoToContent();
    }
  }, [
    filterCategoryId,
    viewMode,
    categoryMemoMinimized,
    updateCategoryMemoMaxHeight,
    fitCategoryMemoToContent,
    applyCategoryMemoMinimizedHeight,
  ]);

  useLayoutEffect(() => {
    if (viewMode !== 'timetable' || !filterCategoryId) return;
    const onViewportResize = () => updateCategoryMemoMaxHeight();
    window.addEventListener('resize', onViewportResize);
    const scrollBody = todoNoteScrollBodyRef.current;
    const ro = scrollBody ? new ResizeObserver(onViewportResize) : null;
    if (scrollBody && ro) ro.observe(scrollBody);
    return () => {
      window.removeEventListener('resize', onViewportResize);
      ro?.disconnect();
    };
  }, [filterCategoryId, viewMode, updateCategoryMemoMaxHeight]);

  useLayoutEffect(() => {
    if (viewMode !== 'timetable' || !filterCategoryId) return;
    const field = categoryMemoFieldRef.current;
    if (!field) return;
    const ro = new ResizeObserver(() => {
      if (categoryMemoApplyingSyncRef.current) return;
      const h = field.offsetHeight;
      const last = categoryMemoLastSyncedHeightRef.current;
      if (last != null && Math.abs(h - last) > 1) {
        categoryMemoUserResizedRef.current = true;
        field.style.overflowY = '';
      }
    });
    ro.observe(field);
    return () => ro.disconnect();
  }, [filterCategoryId, viewMode]);

  useLayoutEffect(() => {
    if (viewMode !== 'timetable' || !filterCategoryId) return;
    return () => {
      const field = categoryMemoFieldRef.current;
      if (field) {
        field.style.height = '';
        field.style.maxHeight = '';
        field.style.overflowY = '';
      }
      categoryMemoUserResizedRef.current = false;
      categoryMemoLastSyncedHeightRef.current = null;
    };
  }, [filterCategoryId, viewMode]);

  const calendarItemsByDate = useMemo(() => {
    const map = new Map<string, TodoNoteItemView[]>();
    for (const it of displayedItems) {
      const key = it.due_date;
      const arr = map.get(key) ?? [];
      arr.push(it);
      map.set(key, arr);
    }
    return map;
  }, [displayedItems]);

  const calendarCells = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const startWeekday = first.getDay();
    const start = new Date(first);
    start.setDate(first.getDate() - startWeekday);
    const out: Date[] = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(d);
    }
    return out;
  }, [calendarMonth]);
  const todayKey = useMemo(() => toYmdKey(new Date()), []);

  useEffect(() => {
    if (viewMode !== 'calendar' || !calendarWorkspaceId) return;
    const from = toYmdKey(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1));
    const to = toYmdKey(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0));
    let alive = true;
    getCalendarDays(calendarWorkspaceId, from, to)
      .then((days) => {
        if (!alive) return;
        const offRows = days.filter((d) => d.day_kind === 'holiday' || d.day_kind === 'closure' || d.day_kind === 'vacation');
        const off = offRows.map((d) => d.day);
        const noteMap: Record<string, string> = {};
        offRows.forEach((d) => {
          const label = (d.note ?? '').trim();
          if (label) noteMap[d.day] = label;
        });
        setHolidayDateSet(new Set(off));
        setHolidayNoteByDate(noteMap);
      })
      .catch(() => {
        if (!alive) return;
        setHolidayDateSet(new Set());
        setHolidayNoteByDate({});
      });
    return () => {
      alive = false;
    };
  }, [viewMode, calendarWorkspaceId, calendarMonth]);

  const selectedBoard = useMemo(
    () => boards.find((b) => b.id === selectedBoardId) ?? null,
    [boards, selectedBoardId]
  );
  const editBoard = useMemo(
    () => boards.find((b) => b.id === editBoardId) ?? null,
    [boards, editBoardId]
  );
  const pageTitle = selectedBoard
    ? formatTodoSharePageTitle(selectedBoard.title)
    : 'TODO 노트';

  const selectBoard = useCallback(
    (boardId: string, opts?: { fromRemote?: boolean }) => {
      const board = boards.find((b) => b.id === boardId);
      if (boardId === selectedBoardId) {
        // 이미 선택된 칩을 다시 눌러도 기억값을 갱신 (LS 손상·레이스 복구)
        if (board) persistSelectedBoard(board);
        else persistSelectedBoard({ id: boardId, title: '' });
        return;
      }
      if (opts?.fromRemote) {
        if (dirty) return;
      } else if (dirty && !window.confirm('저장하지 않은 변경이 있습니다. 노트를 전환할까요?')) {
        return;
      }
      setSelectedBoardId(boardId);
      if (board) persistSelectedBoard(board);
      else persistSelectedBoard({ id: boardId, title: '' });
      if (!opts?.fromRemote) {
        emitTodoNoteBoardSelection({
          noteWorkspaceId,
          boardId,
          source: 'todo-note',
        });
      }
    },
    [selectedBoardId, dirty, boards, persistSelectedBoard, noteWorkspaceId]
  );

  useEffect(() => {
    const onSel = (e: Event) => {
      const d = (e as CustomEvent<TodoNoteBoardSelectionDetail>).detail;
      if (!d?.boardId || d.source === 'todo-note') return;
      const detailWs = d.noteWorkspaceId?.trim() ? d.noteWorkspaceId.trim() : null;
      const pageWs = noteWorkspaceId?.trim() ? noteWorkspaceId.trim() : null;
      if (detailWs !== pageWs) return;
      selectBoard(d.boardId, { fromRemote: true });
    };
    window.addEventListener(TODO_NOTE_BOARD_SELECTION_EVENT, onSel as EventListener);
    return () => window.removeEventListener(TODO_NOTE_BOARD_SELECTION_EVENT, onSel as EventListener);
  }, [noteWorkspaceId, selectBoard]);

  const canCreateTodoBoard = boards.length < MAX_TODO_NOTE_BOARDS;

  const boardPickerChrome = (
    <TodoNoteBoardPicker
      boards={boards}
      selectedBoardId={selectedBoardId}
      open={boardPickerOpen}
      onOpenChange={setBoardPickerOpen}
      onSelect={selectBoard}
      onEdit={(id) => {
        setEditError('');
        setEditBoardId(id);
      }}
      onCreate={() => {
        setCreateError('');
        setCreateOpen(true);
      }}
      canCreate={canCreateTodoBoard}
      maxBoards={MAX_TODO_NOTE_BOARDS}
    />
  );

  const shareModals = (
    <>
      <TodoShareBoardCreateModal
        open={createOpen}
        busy={createBusy}
        error={createError}
        onClose={() => {
          if (!createBusy) setCreateOpen(false);
        }}
        onCreate={async (payload) => {
          if (boards.length >= MAX_TODO_NOTE_BOARDS) {
            setCreateError(`노트는 최대 ${MAX_TODO_NOTE_BOARDS}개까지 만들 수 있습니다.`);
            return;
          }
          setCreateBusy(true);
          setCreateError('');
          try {
            const board = await createTodoShareBoard({
              title: payload.title,
              code: payload.code,
              workspaceId: noteWorkspaceId,
            });
            setBoards((prev) => sortTodoShareBoards([...prev.filter((b) => b.id !== board.id), board]));
            setSelectedBoardId(board.id);
            persistSelectedBoard(board);
            emitTodoNoteBoardSelection({
              noteWorkspaceId,
              boardId: board.id,
              source: 'todo-note',
            });
            setCreateOpen(false);
          } catch (e) {
            setCreateError(e instanceof Error ? e.message : '노트 만들기에 실패했습니다.');
          } finally {
            setCreateBusy(false);
          }
        }}
      />
      <TodoShareBoardEditModal
        open={Boolean(editBoard)}
        board={editBoard}
        busy={editBusy}
        error={editError}
        onClose={() => {
          if (!editBusy) setEditBoardId(null);
        }}
        onCopyLink={() => {
          if (!editBoard) return;
          void navigator.clipboard.writeText(getTodoSharePublicUrl(editBoard.public_token));
        }}
        onToggleActive={() => {
          if (!editBoard) return;
          void (async () => {
            setEditBusy(true);
            setEditError('');
            try {
              const next = await setTodoShareBoardActive(editBoard.id, !editBoard.is_active);
              setBoards((prev) =>
                sortTodoShareBoards(
                  prev.map((x) => (x.id === editBoard.id ? { ...x, ...next } : x))
                )
              );
            } catch (e) {
              setEditError(e instanceof Error ? e.message : '공유 상태 변경 실패');
            } finally {
              setEditBusy(false);
            }
          })();
        }}
        onRename={async (title) => {
          if (!editBoard) return;
          if (isTodoShareDefaultBoard(editBoard)) {
            setEditError('기본 노트 이름은 변경할 수 없습니다.');
            return;
          }
          setEditBusy(true);
          setEditError('');
          try {
            await renameTodoShareBoard(editBoard.id, title);
            setBoards((prev) =>
              sortTodoShareBoards(
                prev.map((x) => (x.id === editBoard.id ? { ...x, title } : x))
              )
            );
          } catch (e) {
            setEditError(e instanceof Error ? e.message : '이름 변경 실패');
          } finally {
            setEditBusy(false);
          }
        }}
        onRotateCode={async (code) => {
          if (!editBoard) return;
          setEditBusy(true);
          setEditError('');
          try {
            const { entry_code } = await rotateTodoShareCode(editBoard.id, code);
            setBoards((prev) =>
              prev.map((x) =>
                x.id === editBoard.id ? { ...x, entry_code: entry_code || code } : x
              )
            );
            window.alert(`입장코드가 변경되었습니다.\n새 코드: ${entry_code || code}`);
          } catch (e) {
            setEditError(e instanceof Error ? e.message : '코드 변경 실패');
            throw e;
          } finally {
            setEditBusy(false);
          }
        }}
        onDelete={async () => {
          if (!editBoard) return;
          if (isTodoShareDefaultBoard(editBoard)) {
            setEditError('기본 노트는 삭제할 수 없습니다.');
            return;
          }
          setEditBusy(true);
          setEditError('');
          try {
            const id = editBoard.id;
            await deleteTodoShareBoard(id);
            invalidateTodoShareBoardContentCache(id);
            setBoards((prev) => {
              const next = sortTodoShareBoards(prev.filter((x) => x.id !== id));
              if (selectedBoardId === id) {
                const fallback = next[0] ?? null;
                setSelectedBoardId(fallback?.id ?? null);
                if (fallback) persistSelectedBoard(fallback);
              }
              return next;
            });
            setEditBoardId(null);
          } catch (e) {
            setEditError(e instanceof Error ? e.message : '삭제 실패');
          } finally {
            setEditBusy(false);
          }
        }}
      />
    </>
  );

  if (boardsLoading) {
    return (
      <section className="cp-todo-note-page" aria-busy="true" aria-label="ToDo 노트 로딩">
        <div className="cp-todo-note-title-row-controls cp-todo-note-board-picker-alone">
          {boardPickerChrome}
        </div>
        <p className="cp-todo-note-muted">불러오는 중…</p>
        {shareModals}
      </section>
    );
  }

  if (!selectedBoardId) {
    return (
      <section className="cp-todo-note-page" aria-labelledby="cp-todo-note-title">
        <div className="cp-todo-note-title-row-controls cp-todo-note-board-picker-alone">
          {boardPickerChrome}
        </div>
        {navError ? <p className="cp-todo-note-error">{navError}</p> : null}
        <div className="cp-todo-share-empty">
          <p className="cp-todo-note-muted">
            {canCreateTodoBoard
              ? '노트가 없습니다. + 새 노트로 만들어 주세요.'
              : `노트는 최대 ${MAX_TODO_NOTE_BOARDS}개까지 만들 수 있습니다.`}
          </p>
        </div>
        {shareModals}
      </section>
    );
  }

  if (loading) {
    return (
      <section className="cp-todo-note-page" aria-busy="true" aria-label="ToDo 노트 로딩">
        <div className="cp-todo-note-title-row-controls cp-todo-note-board-picker-alone">
          {boardPickerChrome}
        </div>
        <p className="cp-todo-note-muted">불러오는 중…</p>
        {shareModals}
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="cp-todo-note-page" role="alert">
        <div className="cp-todo-note-title-row-controls cp-todo-note-board-picker-alone">
          {boardPickerChrome}
        </div>
        <p className="cp-todo-note-error">{loadError}</p>
        {shareModals}
      </section>
    );
  }

  const titleViewToggleChrome = (
    <div className="cp-todo-note-title-row">
      <h2 id="cp-todo-note-title" className="cp-todo-note-title">
        {pageTitle}
        {variant === 'account' ? (
          <span className="cp-todo-note-title-scope"> · 계정</span>
        ) : null}
      </h2>
      <div className="cp-todo-note-title-row-controls">
        <div
          className="cp-todo-note-view-toggle"
          role="tablist"
          aria-label="뷰 전환"
          data-tutorial-todo="view-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'timetable'}
            className={`cp-todo-note-view-btn${viewMode === 'timetable' ? ' is-active' : ''}`}
            data-tutorial-todo="view-timetable"
            onClick={() => setViewMode('timetable')}
          >
            타임테이블
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'calendar'}
            className={`cp-todo-note-view-btn${viewMode === 'calendar' ? ' is-active' : ''}`}
            onClick={() => setViewMode('calendar')}
          >
            캘린더
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'postit'}
            className={`cp-todo-note-view-btn${viewMode === 'postit' ? ' is-active' : ''}`}
            onClick={() => setViewMode('postit')}
          >
            포스트잇
          </button>
        </div>
        {boardPickerChrome}
        <div className="cp-note-header-undo-redo-mobile">
          <ProgramToolbarUndoRedoButtons
            canUndo={undoRedoAvail.canUndo}
            canRedo={undoRedoAvail.canRedo}
            disabled={saving || syncing}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
        </div>
      </div>
    </div>
  );

  const headerActionsChrome = (
    <div className="cp-todo-note-header-actions">
      <WorkspaceFindBar
        searchDraft={searchDraft}
        onSearchDraftChange={handleSearchDraftChange}
        onSubmit={runTodoSearch}
        onClear={clearTodoSearch}
        onPrev={goPrevTodoSearchMatch}
        onNext={goNextTodoSearchMatch}
        showNavSuffix={showSearchNavSuffix}
        activeIndex={activeMatchIndex}
        totalMatches={searchMatches.length}
        inputAriaLabel="ToDo 노트 찾기"
        disabled={loading || saving || syncing}
      />
      <ProgramToolbarUndoRedoButtons
        canUndo={undoRedoAvail.canUndo}
        canRedo={undoRedoAvail.canRedo}
        disabled={saving || syncing}
        onUndo={handleUndo}
        onRedo={handleRedo}
      />
      <button
        type="button"
        className="cp-btn-save-all"
        disabled={saving || autoSaving || syncing || !dirty}
        onClick={() => void handleSave()}
      >
        {saving || autoSaving ? '저장 중…' : '저장하기'}
      </button>
    </div>
  );

  const categoryChipsChrome = (
    <div className="cp-todo-note-chips" role="tablist" aria-label="카테고리 필터">
      <div className={`cp-todo-note-chip-row${filterCategoryId === null ? ' is-active' : ''}`}>
        <button
          type="button"
          role="tab"
          aria-selected={filterCategoryId === null}
          className="cp-todo-note-chip-main"
          onClick={() => setFilterCategoryId(null)}
        >
          전체
        </button>
      </div>
      <DndContext
        sensors={categoryDndSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleCategoryDragEnd}
      >
        <SortableContext
          items={sortedCategories.map((c) => c.id)}
          strategy={horizontalListSortingStrategy}
        >
          {sortedCategories.map((c) => (
            <SortableTodoCategoryChip
              key={c.id}
              id={c.id}
              name={c.name}
              color={c.color}
              active={filterCategoryId === c.id}
              showActions
              onSelect={() => setFilterCategoryId(c.id)}
              onEdit={() => startEditCategory(c.id)}
              onDelete={() => setCategoryDeleteModal({ id: c.id, name: c.name })}
            />
          ))}
        </SortableContext>
      </DndContext>
      <button
        type="button"
        className="cp-todo-note-chip cp-todo-note-chip-add"
        aria-label="카테고리 추가"
        data-tutorial-todo="chip-add"
        onClick={() => {
          setEditingCategoryId(null);
          setCategoryModalOpen(true);
        }}
      >
        +
      </button>
    </div>
  );

  const hideTogglesChrome = (
    <div className="cp-todo-note-toolbar-toggle-wrap">
      {isMobileTodoLayout && viewMode === 'calendar' ? (
        <div
          className="cp-annual-top-saturday-toggle cp-todo-note-calendar-layout-toggle"
          aria-label="캘린더 맞춤·확장"
        >
          <button
            type="button"
            className={
              'team-auto-approve-toggle' +
              (calendarLayoutMode === 'expand' ? ' on' : '')
            }
            onClick={() =>
              setCalendarLayoutMode((m) => (m === 'fit' ? 'expand' : 'fit'))
            }
            aria-pressed={calendarLayoutMode === 'fit'}
            aria-label={
              calendarLayoutMode === 'fit'
                ? '맞춤 모드 — 누르면 확장'
                : '확장 모드 — 누르면 맞춤'
            }
            title={
              calendarLayoutMode === 'fit'
                ? '맞춤: 가로에 일~토 맞춤 표시'
                : '확장: 가로·세로 스크롤로 탐색'
            }
          >
            <span
              className="team-auto-approve-toggle-slider"
              data-on={calendarLayoutMode === 'fit'}
            >
              맞춤
            </span>
            <span
              className="team-auto-approve-toggle-slider"
              data-on={calendarLayoutMode === 'expand'}
            >
              확장
            </span>
          </button>
        </div>
      ) : null}
      {viewMode === 'postit' ? (
        <div className="cp-annual-top-saturday-toggle" aria-label="카테고리 모아보기 토글">
          <span className="cp-weekly-schedule-saturday-toggle-label">
            <span className="cp-todo-note-toggle-label-full">카테고리 모아보기</span>
            <span className="cp-todo-note-toggle-label-short">카테고리</span>
          </span>
          <button
            type="button"
            className={'team-auto-approve-toggle' + (postitGroupByCategory ? ' on' : '')}
            onClick={() => setPostitGroupByCategory((v) => !v)}
            aria-pressed={postitGroupByCategory}
            aria-label="카테고리 모아보기"
            title="켜면 카테고리별로 모으고, 끄면 날짜 순으로 정렬합니다"
          >
            <span className="team-auto-approve-toggle-slider" data-on={!postitGroupByCategory}>
              OFF
            </span>
            <span className="team-auto-approve-toggle-slider" data-on={postitGroupByCategory}>
              ON
            </span>
          </button>
        </div>
      ) : (
        <>
      <div className="cp-annual-top-saturday-toggle" aria-label="루틴 블럭 숨기기 토글">
        <span className="cp-weekly-schedule-saturday-toggle-label">
          <span className="cp-todo-note-toggle-label-full">루틴 블럭 숨기기</span>
          <span className="cp-todo-note-toggle-label-short">루틴 블럭</span>
        </span>
        <button
          type="button"
          className={'team-auto-approve-toggle' + (hideRoutineNotes ? ' on' : '')}
          onClick={() => setHideRoutineNotes((v) => !v)}
          aria-pressed={hideRoutineNotes}
          aria-label="루틴 블럭 숨기기"
        >
          <span className="team-auto-approve-toggle-slider" data-on={!hideRoutineNotes}>
            OFF
          </span>
          <span className="team-auto-approve-toggle-slider" data-on={hideRoutineNotes}>
            ON
          </span>
        </button>
      </div>
      <div className="cp-annual-top-saturday-toggle" aria-label="완료 블럭 숨기기 토글">
        <span className="cp-weekly-schedule-saturday-toggle-label">
          <span className="cp-todo-note-toggle-label-full">완료 블럭 숨기기</span>
          <span className="cp-todo-note-toggle-label-short">완료 블럭</span>
        </span>
        <button
          type="button"
          className={'team-auto-approve-toggle' + (hideCompletedTodos ? ' on' : '')}
          onClick={() => setHideCompletedTodos((v) => !v)}
          aria-pressed={hideCompletedTodos}
          aria-label="완료 블럭 숨기기"
        >
          <span className="team-auto-approve-toggle-slider" data-on={!hideCompletedTodos}>
            OFF
          </span>
          <span className="team-auto-approve-toggle-slider" data-on={hideCompletedTodos}>
            ON
          </span>
        </button>
      </div>
        </>
      )}
    </div>
  );

  const headerChrome = (
    <div className={`cp-todo-note-header${isMobileTodoLayout ? ' cp-todo-note-header--scroll' : ''}`}>
      {titleViewToggleChrome}
      {headerActionsChrome}
    </div>
  );

  const toolbarChrome = (
    <div
      className={
        'cp-todo-note-toolbar cp-todo-note-toolbar--floating-mobile'
      }
    >
      {categoryChipsChrome}
      {hideTogglesChrome}
    </div>
  );

  const calendarMonthNavChrome =
    viewMode === 'calendar' ? (
      <div className="cp-todo-note-calendar-head-shell">
        <button
          type="button"
          className="cp-todo-note-calendar-nav"
          onClick={() => {
            setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
            setSelectedCalendarDate(null);
          }}
          aria-label="이전 달"
        >
          ‹
        </button>
        <button
          type="button"
          className="cp-todo-note-calendar-title cp-todo-note-calendar-title-btn"
          onClick={() => {
            const now = new Date();
            setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
            setSelectedCalendarDate(toYmdKey(now));
          }}
          aria-label="오늘로 이동"
        >
          {calendarMonth.getFullYear()}년 {calendarMonth.getMonth() + 1}월
        </button>
        <button
          type="button"
          className="cp-todo-note-calendar-nav"
          onClick={() => {
            setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
            setSelectedCalendarDate(null);
          }}
          aria-label="다음 달"
        >
          ›
        </button>
      </div>
    ) : null;

  const desktopStickyTopChrome = (
    <div className="cp-todo-note-sticky-top">
      {navError ? <p className="cp-todo-note-error">{navError}</p> : null}
      {headerChrome}
      {saveError ? <p className="cp-todo-note-error">{saveError}</p> : null}
      {toolbarChrome}
    </div>
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
    <section
      className={`cp-todo-note-page${viewMode === 'postit' ? ' is-postit' : ''}${
        viewMode === 'calendar' ? ' is-calendar' : ''
      }`}
      aria-labelledby="cp-todo-note-title"
      onBlurCapture={() => {
        window.setTimeout(() => {
          flushPendingHistory();
        }, 0);
      }}
    >
      <AcademicYearLockNotice label="할일노트" />
      {!isMobileTodoLayout && viewMode !== 'calendar' ? desktopStickyTopChrome : null}
      <div
        ref={todoNoteScrollBodyRef}
        className={`cp-todo-note-scroll-body${
          viewMode === 'calendar' ? ' is-calendar' : viewMode === 'postit' ? ' is-postit' : ''
        }`}
      >
        {!isMobileTodoLayout && viewMode === 'calendar' ? (
          <>
            {desktopStickyTopChrome}
            {calendarMonthNavChrome}
          </>
        ) : null}
        {isMobileTodoLayout ? (
          <>
            <div className="cp-todo-note-board-rail-sticky">
              {calendarMonthNavChrome}
              {navError ? <p className="cp-todo-note-error">{navError}</p> : null}
              <div className="cp-todo-note-header cp-todo-note-header--scroll">
                {titleViewToggleChrome}
              </div>
              {hideTogglesChrome}
            </div>
            {saveError ? <p className="cp-todo-note-error">{saveError}</p> : null}
            <div className="cp-todo-note-mobile-scroll-chrome">
              <div className="cp-todo-note-toolbar cp-todo-note-toolbar--floating-mobile">
                {categoryChipsChrome}
              </div>
            </div>
          </>
        ) : null}
        {viewMode === 'timetable' ? (
          <div ref={timetableWidthSentinelRef} className="cp-todo-note-timetable-width-sentinel" aria-hidden />
        ) : null}
        {viewMode === 'timetable' ? (
          <div ref={timetableHorizInnerRef} className="cp-todo-note-timetable-horiz-inner">
            {filterCategoryId && filterCategory ? (
              <div className="cp-todo-note-category-memo-strip">
                <label htmlFor="cp-todo-note-category-memo-field" className="cp-todo-note-category-memo-label">
                  {filterCategory.name} · 카테고리 메모
                </label>
                <div className="cp-todo-note-category-memo-field-wrap">
                  <button
                    type="button"
                    className="cp-todo-note-category-memo-minimize-btn"
                    onClick={() => {
                      setCategoryMemoMinimized((prev) => {
                        const next = !prev;
                        if (!next) categoryMemoUserResizedRef.current = false;
                        return next;
                      });
                    }}
                    disabled={saving}
                    aria-label={categoryMemoMinimized ? '카테고리 메모 펼치기' : '카테고리 메모 최소화'}
                    title={categoryMemoMinimized ? '메모 전체 높이로 펼치기' : '기본 높이로 최소화'}
                  >
                    <img
                      src={categoryMemoMinimized ? `${process.env.PUBLIC_URL || ''}/Expand.svg` : `${process.env.PUBLIC_URL || ''}/Minimize.svg`}
                      alt=""
                      aria-hidden
                      className="cp-todo-note-category-memo-minimize-icon"
                    />
                  </button>
                  <SearchTextareaMirror
                    textareaRef={categoryMemoFieldRef}
                    id="cp-todo-note-category-memo-field"
                    className={
                      'cp-todo-note-category-memo-field' +
                      (categoryMemoMinimized ? ' is-minimized' : '')
                    }
                    value={filterCategory.note_memo}
                    query={searchQuery}
                    activeMatchId={activeMatchId}
                    buildMatchId={(start) =>
                      buildTodoCategoryNoteSearchMatchId(filterCategoryId!, start)
                    }
                    onChange={(v) => {
                      setCategories((prev) =>
                        prev.map((c) => (c.id === filterCategoryId ? { ...c, note_memo: v } : c))
                      );
                    }}
                    placeholder="이 카테고리용 자유 메모를 입력하세요…"
                    rows={1}
                    disabled={saving}
                    ariaLabel="카테고리 메모"
                  />
                </div>
              </div>
            ) : null}
            <div className="cp-todo-note-column-head" aria-hidden>
              <span className="cp-todo-note-colhead cp-todo-note-colhead--ord">순</span>
              <div className="cp-todo-note-due-cat-head">
                <span className="cp-todo-note-colhead cp-todo-note-colhead--due">마감</span>
                <span className="cp-todo-note-colhead cp-todo-note-colhead--cat">카테고리</span>
              </div>
              <span className="cp-todo-note-colhead cp-todo-note-colhead--memo">메모</span>
              <span className="cp-todo-note-colhead cp-todo-note-colhead--misc">기타</span>
              <span className="cp-todo-note-colhead cp-todo-note-colhead--act" />
            </div>
              <SortableContext items={displayedItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div className="cp-todo-note-list">
                {displayedItems.length === 0 ? (
                  <p className="cp-todo-note-muted">표시할 블럭이 없습니다. 블럭 추가를 눌러 보세요.</p>
                ) : (
                  displayedItems.map((it, idx) => {
                    const parentId = it.routineParentId ?? it.id;
                    const isRoutineOcc = Boolean(it.routineParentId);
                    return (
                    <SortableRow
                      key={it.id}
                      item={it}
                      ord={idx + 1}
                      categories={categories}
                      categoryInput={categoryInputByItemId[parentId] ?? categoryInputByItemId[it.id] ?? ''}
                      onCategoryInput={(v) => {
                        setCategoryInputByItemId((m) => ({ ...m, [parentId]: v }));
                        if (v.trim().length >= 2) {
                          const hit = categories.find((c) => c.name.toLowerCase().startsWith(v.trim().toLowerCase()));
                          if (hit) patchViewItemFields(it.id, { category_id: hit.id });
                        }
                      }}
                      onPickCategory={(cid) => {
                        patchViewItemFields(it.id, { category_id: cid });
                        const c = cid ? categories.find((x) => x.id === cid) : null;
                        setCategoryInputByItemId((m) => ({ ...m, [parentId]: c?.name ?? '' }));
                      }}
                      onDueDate={(iso) => patchViewItemFields(it.id, { due_date: iso })}
                      onDueSwitchToMonthOnly={(iso) => patchViewItemFields(it.id, { due_kind: 'month', due_date: iso })}
                      onDueSwitchToDay={(iso) => patchViewItemFields(it.id, { due_kind: 'day', due_date: iso })}
                      onMemoPatch={(title, body) => patchViewItemFields(it.id, { title, body })}
                      onMisc={(v) => patchViewItemFields(it.id, { misc: v })}
                      onToggleComplete={() => toggleViewComplete(it.id)}
                      onRemove={() => requestRemoveItem(it.id)}
                      onDuplicate={isRoutineOcc ? undefined : () => duplicateItemBelow(it.id)}
                      onEditRoutine={isRoutineOcc ? () => openRoutineEdit(parentId) : undefined}
                      dragDisabled={it.completed || filterCategoryId !== null || isRoutineOcc}
                      selected={selectedTimetableItemId === it.id}
                      onSelect={() => setSelectedTimetableItemId(it.id)}
                      searchQuery={searchQuery}
                      activeMatchId={activeMatchId}
                      completeMark={it.completeMark}
                      routineBadge={isRoutineOcc}
                    />
                    );
                  })
                )}
              </div>
              </SortableContext>
          </div>
        ) : viewMode === 'calendar' ? (
          <div
            className={
              'cp-todo-note-calendar-wrap' +
              (isMobileTodoLayout && calendarLayoutMode === 'fit'
                ? ' is-fit'
                : ' is-expand')
            }
          >
            <div className="cp-todo-note-calendar-grid cp-todo-note-calendar-grid--weekday" aria-hidden>
              {['일', '월', '화', '수', '목', '금', '토'].map((w) => (
                <div key={w} className="cp-todo-note-calendar-weekday">
                  {w}
                </div>
              ))}
            </div>
            <div className="cp-todo-note-calendar-grid">
              {calendarCells.map((cellDate) => {
                const key = toYmdKey(cellDate);
                const inMonth = cellDate.getMonth() === calendarMonth.getMonth();
                const dayOfWeek = cellDate.getDay();
                const isSunday = dayOfWeek === 0;
                const isSaturday = dayOfWeek === 6;
                const isWeekend = isSunday || isSaturday;
                const isHoliday = holidayDateSet.has(key) || isWeekend;
                const isToday = key === todayKey;
                const holidayLabel = holidayNoteByDate[key] ?? '';
                const dayItems = (calendarItemsByDate.get(key) ?? []).filter((it) => it.title.trim().length > 0);
                const isDateSelected = selectedCalendarDate === key;
                const useTwoColCalendarItems = dayItems.length >= 5;
                const searchPinnedDayItems = searchQuery
                  ? dayItems.filter((it) => todoItemCalendarPopSearchHit(it, searchQuery))
                  : [];
                const useSearchPopRow = searchPinnedDayItems.length > 1;
                return (
                  <div
                    key={key}
                    className={`cp-todo-note-calendar-cell${inMonth ? ' is-current-month' : ' is-outside'}${
                      isHoliday ? ' cp-todo-note-calendar-cell--holiday' : ''
                    }${isToday ? ' is-today' : ''}${selectedCalendarDate === key ? ' is-selected' : ''}${
                      useSearchPopRow ? ' has-search-pop-row' : ''
                    }`}
                    role="button"
                    tabIndex={inMonth ? 0 : -1}
                    aria-disabled={!inMonth}
                    aria-current={isToday ? 'date' : undefined}
                    onClick={() => {
                      if (!inMonth) return;
                      setSelectedCalendarDate(key);
                    }}
                    onKeyDown={(e) => {
                      if (!inMonth) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedCalendarDate(key);
                      }
                    }}
                  >
                    <div className="cp-todo-note-calendar-day-row">
                      <div
                        className={`cp-todo-note-calendar-day${isToday ? ' is-today' : ''}${
                          isSaturday
                            ? ' cp-todo-note-calendar-day--sat'
                            : isHoliday
                              ? ' cp-todo-note-calendar-day--holiday'
                              : ''
                        }`}
                      >
                        {cellDate.getDate()}
                      </div>
                    </div>
                    {holidayLabel ? <div className="cp-todo-note-calendar-holiday-note">{holidayLabel}</div> : null}
                    <div
                      className={`cp-todo-note-calendar-items${
                        useTwoColCalendarItems ? ' cp-todo-note-calendar-items--two-col' : ''
                      }`}
                    >
                      {dayItems.map((it) => {
                        const cat = it.category_id ? categoryMap.get(it.category_id) : null;
                        const catColor = cat?.color?.trim() || '#c0c4cc';
                        const gaugeProgress =
                          calendarItemHoldGauge?.itemId === it.id ? calendarItemHoldGauge.progress : 0;
                        const calendarSearchPinned =
                          Boolean(searchQuery) && todoItemCalendarPopSearchHit(it, searchQuery);
                        return (
                          <div
                            key={it.id}
                            className={`cp-todo-note-calendar-item${it.completed ? ' is-completed' : ''}${
                              it.completeMark === 'x' ? ' is-auto-missed' : ''
                            }${it.routineParentId ? ' is-routine' : ''}${
                              isDateSelected ? ' is-clickable' : ''
                            }${gaugeProgress > 0 ? ' is-holding' : ''}${
                              calendarSearchPinned ? ' cp-todo-note-calendar-item--search-hit' : ''
                            }`}
                            style={{ ['--todo-cal-cat-color' as string]: catColor }}
                            role={isDateSelected ? 'button' : undefined}
                            tabIndex={isDateSelected ? 0 : -1}
                            onPointerDown={(e) => {
                              if (!isDateSelected) return;
                              if (e.pointerType === 'mouse' && e.button !== 0) return;
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                e.currentTarget.setPointerCapture(e.pointerId);
                              } catch {
                                /* noop */
                              }
                              scheduleCalendarItemHoldGauge(it.id);
                            }}
                            onPointerUp={(e) => {
                              if (!isDateSelected) return;
                              e.stopPropagation();
                              releaseCalendarItemPointer(it.id, e.currentTarget, e.pointerId);
                            }}
                            onPointerCancel={(e) => {
                              if (!isDateSelected) return;
                              e.stopPropagation();
                              releaseCalendarItemPointer(it.id, e.currentTarget, e.pointerId);
                            }}
                            onPointerLeave={(e) => {
                              if (!isDateSelected) return;
                              if (
                                calendarHoldDelayTimerRef.current != null ||
                                calendarHoldStartRef.current?.itemId === it.id
                              ) {
                                releaseCalendarItemPointer(it.id, e.currentTarget, e.pointerId);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (!isDateSelected) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                openCalendarItemEditModal(it.id);
                              }
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                          >
                            <div
                              className="cp-todo-note-calendar-item-hold-gauge"
                              style={{ transform: `scaleX(${gaugeProgress})` }}
                              aria-hidden
                            />
                            <span
                              className="cp-todo-note-calendar-item-dot"
                              style={{ background: catColor }}
                            />
                            <span className="cp-todo-note-calendar-item-title">
                              {searchQuery ? (
                                <SearchHighlightedText
                                  text={it.title}
                                  query={searchQuery}
                                  activeMatchId={activeMatchId}
                                  buildMatchId={(start) =>
                                    buildTodoItemFieldSearchMatchId(it.id, 'memo', start)
                                  }
                                />
                              ) : (
                                it.title
                              )}
                            </span>
                            {!useSearchPopRow ? (
                              <TodoNoteCalendarItemHoverPop pinned={calendarSearchPinned}>
                                <TodoNoteCalendarItemPopContent
                                  item={it}
                                  searchQuery={searchQuery}
                                  activeMatchId={activeMatchId}
                                />
                              </TodoNoteCalendarItemHoverPop>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    {useSearchPopRow ? (
                      <div className="cp-todo-note-calendar-cell-pops-row">
                        {searchPinnedDayItems.map((it) => (
                          <TodoNoteCalendarItemHoverPop key={it.id} inRow pinned>
                            <TodoNoteCalendarItemPopContent
                              item={it}
                              searchQuery={searchQuery}
                              activeMatchId={activeMatchId}
                            />
                          </TodoNoteCalendarItemHoverPop>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : selectedBoardId ? (
          <TodoNotePostitBoard
            boardId={selectedBoardId}
            items={postitItems}
            categories={categories}
            highlightCategoryId={filterCategoryId}
            groupByCategory={postitGroupByCategory}
            onDuplicate={openDuplicateItemModal}
            onToggleFavorite={toggleItemFavorite}
            onOpen={openCalendarItemEditModal}
            todayYmd={todayYmd}
            fullPage
          />
        ) : null}
      </div>

      <TodoCategoryCreateModal
        isOpen={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setEditingCategoryId(null);
        }}
        onConfirm={addCategoryFromModal}
        initialName={editingCategoryId ? categories.find((c) => c.id === editingCategoryId)?.name ?? '' : ''}
        initialColor={
          editingCategoryId ? categories.find((c) => c.id === editingCategoryId)?.color ?? DEFAULT_TODO_CATEGORY_COLOR : undefined
        }
        categoryId={editingCategoryId}
        title={
          editingCategoryId && defaultCategory?.id === editingCategoryId
            ? '일반 카테고리 색상'
            : editingCategoryId
              ? '카테고리 수정'
              : '카테고리 만들기'
        }
        confirmLabel={editingCategoryId ? '수정' : '추가'}
        nameEditable={!(editingCategoryId && defaultCategory?.id === editingCategoryId)}
      />
      {categoryDeleteModal ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => e.target === e.currentTarget && setCategoryDeleteModal(null)}
        >
          <div
            className="cp-modal cp-admin-learning-template-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-todo-note-category-delete-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="cp-todo-note-category-delete-title">카테고리 삭제</h3>
            <div className="cp-modal-body">
              <p>
                `{categoryDeleteModal.name}` 카테고리를 삭제하면 해당 카테고리에 속한 ToDo 블럭은 기본 카테고리(
                {DEFAULT_TODO_CATEGORY_NAME})로 옮겨집니다.
                <br />
                계속하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setCategoryDeleteModal(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  removeCategory(categoryDeleteModal.id);
                  setCategoryDeleteModal(null);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {routineDeleteConfirmId
        ? createPortal(
            <div
              className="cp-pin-confirm-overlay cp-pin-confirm-overlay--viewport"
              role="presentation"
              onClick={() => setRoutineDeleteConfirmId(null)}
            >
              <div
                className="cp-pin-confirm cp-pin-confirm--warn"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cp-todo-note-routine-delete-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="cp-pin-confirm-warn-icon" aria-hidden>
                  !
                </div>
                <p id="cp-todo-note-routine-delete-title" className="cp-pin-confirm-warn-title">
                  루틴 삭제
                </p>
                <p className="cp-pin-confirm-warn-desc">
                  이 루틴 블럭을 삭제하면
                  <br />
                  모든 날짜의 루틴 블럭 기록이 함께 삭제됩니다.
                  <br />
                  계속하시겠습니까?
                </p>
                <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
                  <button
                    type="button"
                    className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                    onClick={() => setRoutineDeleteConfirmId(null)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                    onClick={() => {
                      removeItemFixed(routineDeleteConfirmId);
                      setRoutineDeleteConfirmId(null);
                    }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {itemAddModalOpen && itemAddDraft ? (
        <TodoNoteItemAddModal
          open={itemAddModalOpen}
          draft={itemAddDraft}
          categories={categories}
          categoryInput={itemAddCategoryInput}
          title={itemAddContext && 'duplicate' in itemAddContext && itemAddContext.duplicate ? 'TODO 복제' : 'TODO 추가'}
          cancelLabel="닫기"
          saveLabel={itemAddContext && 'duplicate' in itemAddContext && itemAddContext.duplicate ? '저장' : '확인'}
          discardConfirmOpen={itemAddDiscardConfirmOpen}
          onDismissBackdrop={tryDismissItemAddModal}
          onCategoryInput={(v) => {
            setItemAddCategoryInput(v);
            if (v.trim().length >= 2) {
              const hit = categories.find((c) => c.name.toLowerCase().startsWith(v.trim().toLowerCase()));
              if (hit) patchItemAddDraft({ category_id: hit.id });
            }
          }}
          onPickCategory={(cid) => {
            patchItemAddDraft({ category_id: cid });
            const c = cid ? categories.find((x) => x.id === cid) : null;
            setItemAddCategoryInput(c?.name ?? '');
          }}
          onDueDate={(iso) => patchItemAddDraft({ due_date: iso })}
          onDueSwitchToMonthOnly={(iso) => patchItemAddDraft({ due_kind: 'month', due_date: iso })}
          onDueSwitchToDay={(iso) => patchItemAddDraft({ due_kind: 'day', due_date: iso })}
          onMemoPatch={(title, body) => patchItemAddDraft({ title, body })}
          onMisc={(v) => patchItemAddDraft({ misc: v })}
          onCancel={tryDismissItemAddModal}
          onSave={submitItemAddModal}
          onDiscardConfirmClose={() => setItemAddDiscardConfirmOpen(false)}
          onDiscardConfirmDismiss={closeItemAddModal}
        />
      ) : null}
      {itemEditModalOpen && itemEditDraft ? (
        <TodoNoteItemAddModal
          open={itemEditModalOpen}
          draft={itemEditDraft}
          categories={categories}
          categoryInput={itemEditCategoryInput}
          title="TODO 수정"
          saveLabel="확인"
          cancelLabel="닫기"
          autoFocusMemo={false}
          showHeaderCompleteToggle
          onToggleComplete={() => {
            setItemEditCompleteTouched(true);
            if (itemEditDraft.completed) {
              patchItemEditDraft({ completed: false });
              setItemEditCompleteMark(null);
            } else {
              patchItemEditDraft({ completed: true });
              setItemEditCompleteMark('v');
            }
          }}
          discardConfirmOpen={itemEditDiscardConfirmOpen}
          onDismissBackdrop={tryDismissItemEditModal}
          onCategoryInput={(v) => {
            setItemEditCategoryInput(v);
            if (v.trim().length >= 2) {
              const hit = categories.find((c) => c.name.toLowerCase().startsWith(v.trim().toLowerCase()));
              if (hit) patchItemEditDraft({ category_id: hit.id });
            }
          }}
          onPickCategory={(cid) => {
            patchItemEditDraft({ category_id: cid });
            const c = cid ? categories.find((x) => x.id === cid) : null;
            setItemEditCategoryInput(c?.name ?? '');
          }}
          onDueDate={(iso) => {
            if (itemEditOccurrenceYmd) return;
            patchItemEditDraft({ due_date: iso });
          }}
          onDueSwitchToMonthOnly={(iso) => {
            if (itemEditOccurrenceYmd) return;
            patchItemEditDraft({ due_kind: 'month', due_date: iso });
          }}
          onDueSwitchToDay={(iso) => {
            if (itemEditOccurrenceYmd) return;
            patchItemEditDraft({ due_kind: 'day', due_date: iso });
          }}
          onMemoPatch={(title, body) => patchItemEditDraft({ title, body })}
          onMisc={(v) => patchItemEditDraft({ misc: v })}
          onCancel={tryDismissItemEditModal}
          onSave={submitItemEditModal}
          onDiscardConfirmClose={() => setItemEditDiscardConfirmOpen(false)}
          onDiscardConfirmDismiss={closeItemEditModal}
          completeMark={itemEditCompleteMark}
          onDelete={() => {
            if (!itemEditItemId) return;
            removeItemFixed(itemEditItemId);
            closeItemEditModal();
          }}
        />
      ) : null}
      <TodoRoutineCreateModal
        open={routineAddModalOpen}
        draft={routineAddDraft}
        categories={sortedCategories}
        categoryInput={routineAddCategoryInput}
        mode={routineModalMode}
        initialRoutine={routineModalMode === 'edit' ? routineAddDraft?.routine ?? null : null}
        occurrenceYmd={routineEditOccurrenceYmd}
        completeToggleMode={
          routineModalMode === 'edit' && routineEditOccurrenceYmd ? 'always' : 'never'
        }
        completeMark={routineEditCompleteMark}
        onToggleComplete={() => {
          if (!routineAddDraft) return;
          setRoutineEditCompleteTouched(true);
          if (routineAddDraft.completed) {
            setRoutineAddDraft((d) => (d ? { ...d, completed: false } : d));
            setRoutineEditCompleteMark(null);
          } else {
            setRoutineAddDraft((d) => (d ? { ...d, completed: true } : d));
            setRoutineEditCompleteMark('v');
          }
        }}
        onDelete={
          routineModalMode === 'edit' && routineAddDraft?.id
            ? () => {
                const id = routineAddDraft.id;
                closeRoutineModal();
                setRoutineDeleteConfirmId(id);
              }
            : undefined
        }
        onDismissBackdrop={closeRoutineModal}
        onCategoryInput={setRoutineAddCategoryInput}
        onPickCategory={(cid) => {
          setRoutineAddDraft((d) => (d ? { ...d, category_id: cid } : d));
          const name = cid ? categories.find((c) => c.id === cid)?.name : '';
          if (name) setRoutineAddCategoryInput(name);
        }}
        onMemoPatch={(title, body) => setRoutineAddDraft((d) => (d ? { ...d, title, body } : d))}
        onMisc={(v) => setRoutineAddDraft((d) => (d ? { ...d, misc: v } : d))}
        onCancel={closeRoutineModal}
        onSave={({ draft, routine }) => {
          if (!userId) return;
          if (routineModalMode === 'edit' && draft.id) {
            const prev = items.find((x) => x.id === draft.id);
            const prevLogs = prev?.routine?.dayLogs ?? {};
            let nextParent: TodoNoteItemDraft = {
              ...(prev ?? draft),
              title: draft.title,
              body: draft.body,
              misc: draft.misc,
              category_id: draft.category_id,
              is_routine: true,
              completed: false,
              routine: {
                ...routine,
                dayLogs: normalizeRoutineDayLogs(prevLogs),
              },
            };
            if (routineEditOccurrenceYmd && routineEditCompleteTouched) {
              nextParent = patchRoutineDayLog(nextParent, routineEditOccurrenceYmd, {
                completedOverride: draft.completed,
              });
            }
            patchItem(draft.id, {
              title: nextParent.title,
              body: nextParent.body,
              misc: nextParent.misc,
              category_id: nextParent.category_id,
              is_routine: true,
              completed: false,
              routine: nextParent.routine,
            });
            const catName = draft.category_id
              ? categories.find((c) => c.id === draft.category_id)?.name ?? ''
              : '';
            setCategoryInputByItemId((m) => ({ ...m, [draft.id]: catName }));
            closeRoutineModal();
            return;
          }
          const newId = generateId();
          const categoryId = draft.category_id ?? defaultCategory?.id ?? null;
          const finalItem: TodoNoteItemDraft = {
            ...draft,
            id: newId,
            user_id: userId,
            workspace_id: noteWorkspaceId,
            category_id: categoryId,
            due_kind: 'day',
            due_date: toISODate(new Date()),
            completed: false,
            is_routine: true,
            routine,
            bucket_order: 0,
          };
          setItems((prev) => {
            const bk = todoItemBucketKey(finalItem);
            const bo = maxBucketOrderIn(prev, bk) + 1;
            return [...prev, { ...finalItem, bucket_order: bo }];
          });
          const catName = categoryId ? categories.find((c) => c.id === categoryId)?.name ?? '' : '';
          setCategoryInputByItemId((m) => ({ ...m, [newId]: catName }));
          closeRoutineModal();
        }}
      />
      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSave()}
        disabled={saving || autoSaving || syncing || !dirty}
        aria-label="저장하기"
        title={dirty ? '저장하기' : '저장할 변경사항 없음'}
      >
        <img src={`${process.env.PUBLIC_URL || ''}/Save.svg`} alt="" className="cp-fab-save-icon" />
      </button>
      <button
        type="button"
        className="cp-todo-note-fab-routine"
        data-tutorial-todo="fab-routine"
        onClick={() => {
          if (!userId) return;
          const baseCategory =
            (filterCategoryId ? categories.find((c) => c.id === filterCategoryId) : null) ??
            defaultCategory ??
            null;
          setRoutineModalMode('create');
          setRoutineEditOccurrenceYmd(null);
          setRoutineEditCompleteMark(null);
          setRoutineEditCompleteTouched(false);
          setRoutineAddDraft({
            id: `__routine-draft-${generateId()}`,
            user_id: userId,
            workspace_id: noteWorkspaceId,
            category_id: baseCategory?.id ?? null,
            due_kind: 'day',
            due_date: toISODate(new Date()),
            title: '',
            body: '',
            misc: '',
            completed: false,
            bucket_order: 0,
            is_routine: true,
          });
          setRoutineAddCategoryInput(baseCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME);
          setRoutineAddModalOpen(true);
        }}
        aria-label="루틴 과제 추가"
        title="루틴 과제 추가"
      >
        <span className="cp-todo-note-fab-routine-label">루틴</span>
        <span className="cp-todo-note-fab-routine-plus" aria-hidden>
          +
        </span>
      </button>
      <button
        type="button"
        className="cp-todo-note-fab-add"
        data-tutorial-todo="fab-add"
        onClick={viewMode === 'calendar' ? openCalendarAddModal : openTimetableAddModal}
        aria-label="메모 추가"
        title="메모 추가"
      >
        +
      </button>
      {shareModals}
    </section>
    </DndContext>
  );
};

export default TodoNotePage;
